import { useEffect, useRef, useState } from "react";
import useStore from "../../Store/Store";
import { RepoTree as RepoTreeType } from "../../utils/RepoTree";
import { logger } from "../../utils/Logger";
import { getFileContent } from "../SourceDoc/SourceDocHandlers";
import { getChatCompletion } from "../../Api/ServerInterface";
import { processUsecaseDescription } from "../../utils/MarkdownProcessor";
import { aiWorkflowLogger } from "../../utils/AIWorkflowLogger";
import {
  FILE_SIZE_THRESHOLDS,
  estimateTokenCount,
  exceedsThresholds,
  extractSnippetAroundMatches,
  calculateTotalTokens,
} from "../../utils/TokenCounter";
import { smartContextExtractor } from "../../utils/SmartContextExtractor";
import {
  generateRepositoryDiagram,
  AutoRepoDiagramOptions,
  needsRepositoryDiagramGeneration,
} from "./AutoRepoDiagramGenerator";

export interface ReadmeFile {
  path: string;
  content: string;
}

export interface SuggestedFile {
  filePath: string;
  reasonForPrioritizing: string;
}

interface UsecaseFeature {
  usecaseTitle: string;
  usecaseKeywords: string;
  usecaseDescription: string;
}

/**
 * Asynchronously returns up to `numberOfFiles` README.md objects. Each object
 * contains the absolute repository `path` and the file `content`.
 *
 * If the file content is not already present in `repoData`, this helper will
 * retrieve it using the same logic employed throughout the app (GitHub REST
 * call via `getFileContentFromGithub` for remote sessions or `openFile` for
 * local sessions).  Retrieved content is cached back into `repoData` via
 * `setFileContent` so subsequent calls are instantaneous.
 */
export const getTopXReadmeFiles = async (
  numberOfFiles: number
): Promise<ReadmeFile[]> => {
  if (numberOfFiles <= 0) return [];

  const { repoTree, repoData, setFileContent } = useStore.getState();

  // Guard against the tree not being ready yet
  if (!repoTree) {
    logger.debug.warn("getTopXReadmeFiles called before repoTree is available");
    return [];
  }

  logger.debug.info(`Starting search for top ${numberOfFiles} README.md files`);

  // -------------------------------------------------------------------
  // 1. Locate the N closest README files using BFS
  // -------------------------------------------------------------------
  const queue: { node: any }[] = [{ node: (repoTree as RepoTreeType).root }];
  const paths: string[] = [];

  while (queue.length && paths.length < numberOfFiles) {
    const { node } = queue.shift()!;

    // Match any README.* file (case-insensitive) on blob files only.
    if (node.type === "blob" && /^README\.[^.]+$/i.test(node.name)) {
      paths.push(node.path);
      logger.debug.info(`Found README file: ${node.path}`);
      if (paths.length === numberOfFiles) break;
    }

    // Breadth-first traversal – enqueue children sorted alphabetically for deterministic order.
    const children = Array.from(node.children.values()).sort((a: any, b: any) =>
      a.name.localeCompare(b.name)
    );
    children.forEach((child: any) => queue.push({ node: child }));
  }

  logger.debug.info(`Located ${paths.length} README files in repository tree`);

  // -------------------------------------------------------------------
  // 2. Resolve file contents (using cache where possible)
  // -------------------------------------------------------------------
  const readmeObjects: ReadmeFile[] = [];

  for (const path of paths) {
    try {
      let content: any =
        repoData[path]?.fileContent ||
        // legacy key used in some parts of the codebase
        repoData[path]?.contents;

      if (!content) {
        logger.debug.info(`Retrieving content for README file: ${path}`);
        // Retrieve and cache the content. Re-use existing helper to honour
        // GitHub vs. local session logic.
        content = await getFileContent(path);

        // getFileContent already stores the value via setFileContent but do a
        // defensive cache in case implementation changes.
        if (content) {
          setFileContent(path, content);
        }
      } else {
        logger.debug.info(`Using cached content for README file: ${path}`);
      }

      // handle array representation (content split by lines)
      if (Array.isArray(content)) {
        content = content.join("\n");
      }

      // Ensure we only push valid entries
      if (typeof content === "string") {
        readmeObjects.push({ path, content });
        logger.debug.info(
          `Successfully loaded README file: ${path} (${content.length} characters)`
        );
      }
    } catch (error) {
      // Log but continue processing remaining paths
      logger.debug.warn(
        `Failed to retrieve contents for README at path '${path}':`,
        error
      );
    }
  }

  logger.debug.info(
    `Retrieved ${readmeObjects.length} README.md file(s) closest to root.`
  );

  return readmeObjects;
};

/**
 * Attempts to coerce the raw LLM output into the strict
 * `SuggestedFile[]` format expected by the UI.  The helper is intentionally
 * defensive – the model might wrap JSON in triple back-ticks or include
 * explanatory prose.  Inspired by common JSON-extraction patterns and
 * leveraging simple `Object.keys` iteration techniques [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/keys) and [GfG guide](https://www.geeksforgeeks.org/how-to-create-array-of-objects-from-keys-values-of-another-object-in-javascript/).
 */
const parseSuggestedFiles = (raw: string): SuggestedFile[] | null => {
  try {
    if (!raw) throw new Error("No data to parse");

    let jsonPayload = raw.trim();

    // Strategy 1: Try to parse the JSON directly first (most common case)
    try {
      const parsed = JSON.parse(jsonPayload);
      if (Array.isArray(parsed)) {
        return processValidParsedData(parsed);
      }
      // If it's an object with a property that's an array, try to extract it
      if (typeof parsed === "object" && parsed !== null) {
        const arrayProps = Object.values(parsed).filter(Array.isArray);
        if (arrayProps.length === 1) {
          return processValidParsedData(arrayProps[0]);
        }
      }
    } catch (directParseError) {
      logger.debug.info("Direct JSON parse failed, trying other strategies");
    }

    // Strategy 2: Look for JSON wrapped in code blocks
    const codeBlockMatch = jsonPayload.match(
      /```(?:json)?[\s\r\n]*([\s\S]*?)```/i
    );
    if (codeBlockMatch) {
      try {
        jsonPayload = codeBlockMatch[1].trim();
        const parsed = JSON.parse(jsonPayload);
        if (Array.isArray(parsed)) {
          return processValidParsedData(parsed);
        }
      } catch (codeBlockParseError) {
        logger.debug.info("Code block JSON parse failed, trying next strategy");
      }
    }

    // Strategy 3: Look for JSON anywhere in the text (multiple potential JSON blocks)
    const jsonMatches = jsonPayload.match(/\[[\s\S]*?\]/g);
    if (jsonMatches) {
      for (const match of jsonMatches) {
        try {
          const parsed = JSON.parse(match);
          if (Array.isArray(parsed)) {
            const result = processValidParsedData(parsed);
            if (result && result.length > 0) {
              return result;
            }
          }
        } catch (matchParseError) {
          continue; // Try next match
        }
      }
    }

    // Strategy 4: Try to fix common JSON formatting issues
    try {
      logger.debug.warn("Attempting to fix malformed JSON");

      let fixedPayload = jsonPayload;

      // Remove any leading/trailing non-JSON text
      const arrayStartIndex = fixedPayload.indexOf("[");
      const arrayEndIndex = fixedPayload.lastIndexOf("]");

      if (
        arrayStartIndex !== -1 &&
        arrayEndIndex !== -1 &&
        arrayEndIndex > arrayStartIndex
      ) {
        fixedPayload = fixedPayload.substring(
          arrayStartIndex,
          arrayEndIndex + 1
        );
      }

      // Fix incomplete strings by looking for patterns where a string value is broken
      fixedPayload = fixedPayload.replace(
        /("reasonForPrioritizing"\s*:\s*"[^"]*)"[\s\n]+([^,}\]]+)"/g,
        '$1 $2"'
      );

      // Remove trailing commas
      fixedPayload = fixedPayload.replace(/,(\s*[}\]])/g, "$1");

      // Ensure the JSON array is properly closed
      if (!fixedPayload.trim().endsWith("]")) {
        // Count opening and closing brackets
        const openBrackets = (fixedPayload.match(/\[/g) || []).length;
        const closeBrackets = (fixedPayload.match(/\]/g) || []).length;

        if (openBrackets > closeBrackets) {
          // Try to close any open objects and the array
          const openBraces = (fixedPayload.match(/{/g) || []).length;
          const closeBraces = (fixedPayload.match(/}/g) || []).length;

          if (openBraces > closeBraces) {
            fixedPayload += '"}'; // Close the string and object
          }
          fixedPayload += "]"; // Close the array
        }
      }

      // Try parsing the fixed JSON
      const parsed = JSON.parse(fixedPayload);
      if (Array.isArray(parsed)) {
        return processValidParsedData(parsed);
      }
    } catch (fixedParseError) {
      logger.debug.info(
        "Fixed JSON parse also failed, trying regex extraction"
      );
    }

    // Strategy 5: Extract valid JSON objects using regex (most aggressive approach)
    const objectMatches = jsonPayload.matchAll(
      /\{\s*"filePath"\s*:\s*"([^"]+)"\s*,\s*"reasonForPrioritizing"\s*:\s*"([^"]+)"\s*\}/g
    );

    const extracted: SuggestedFile[] = [];
    for (const match of objectMatches) {
      if (match[1] && match[2]) {
        extracted.push({
          filePath: match[1],
          reasonForPrioritizing: match[2],
        });
      }
    }

    if (extracted.length > 0) {
      logger.debug.info(
        `Extracted ${extracted.length} valid entries using regex fallback`
      );
      return extracted;
    }

    // Strategy 6: Even more aggressive regex to handle multiline strings
    const aggressiveMatches = jsonPayload.matchAll(
      /"filePath"\s*:\s*"([^"]+)"[\s\S]*?"reasonForPrioritizing"\s*:\s*"([^"]*(?:"[^"]*"[^"]*)*[^"]*)"/g
    );

    const aggressiveExtracted: SuggestedFile[] = [];
    for (const match of aggressiveMatches) {
      if (match[1] && match[2]) {
        aggressiveExtracted.push({
          filePath: match[1],
          reasonForPrioritizing: match[2].replace(/\\n/g, " ").trim(),
        });
      }
    }

    if (aggressiveExtracted.length > 0) {
      logger.debug.info(
        `Extracted ${aggressiveExtracted.length} valid entries using aggressive regex fallback`
      );
      return aggressiveExtracted;
    }

    throw new Error("All parsing strategies failed");
  } catch (error) {
    logger.error("Failed to parse LLM suggested files JSON:", error);
    logger.error("Raw response:", raw);
    return null;
  }
};

// Helper function to process valid parsed data
const processValidParsedData = (parsed: any[]): SuggestedFile[] | null => {
  // Validate & normalise each entry
  const normalised: SuggestedFile[] = [];

  parsed.forEach((item: any, idx: number) => {
    if (!item || typeof item !== "object") return;

    // Accept slight key variations just in case (defensive coding)
    const filePath =
      item.filePath || item.path || item.file || item.filename || item.name;
    const reason =
      item.reasonForPrioritizing || item.reason || item.description || item.why;

    if (typeof filePath === "string" && typeof reason === "string") {
      normalised.push({ filePath, reasonForPrioritizing: reason });
    } else {
      logger.debug.warn(
        `Discarding malformed entry at index ${idx} in LLM response.`
      );
    }
  });

  return normalised.length ? normalised : null;
};

/**
 * Attempts to coerce the raw LLM output into the strict `UsecaseFeature[]`
 * format expected by the UI. Extremely robust to handle various edge cases.
 */
const parseUsecaseFeatures = (raw: string): UsecaseFeature[] | null => {
  try {
    if (!raw) throw new Error("No data to parse");

    let jsonPayload = raw.trim();

    // Strategy 1: Try to parse the JSON directly first (most common case)
    try {
      const parsed = JSON.parse(jsonPayload);
      if (Array.isArray(parsed)) {
        return processValidUsecaseData(parsed);
      }
      // If it's an object with a property that's an array, try to extract it
      if (typeof parsed === "object" && parsed !== null) {
        const arrayProps = Object.values(parsed).filter(Array.isArray);
        if (arrayProps.length === 1) {
          return processValidUsecaseData(arrayProps[0]);
        }
      }
    } catch (directParseError) {
      logger.debug.info(
        "Direct JSON parse failed for usecases, trying other strategies"
      );
    }

    // Strategy 2: Look for JSON wrapped in code blocks
    const codeBlockMatch = jsonPayload.match(
      /```(?:json)?[\s\r\n]*([\s\S]*?)```/i
    );
    if (codeBlockMatch) {
      try {
        jsonPayload = codeBlockMatch[1].trim();
        const parsed = JSON.parse(jsonPayload);
        if (Array.isArray(parsed)) {
          return processValidUsecaseData(parsed);
        }
      } catch (codeBlockParseError) {
        logger.debug.info(
          "Code block JSON parse failed for usecases, trying next strategy"
        );
      }
    }

    // Strategy 3: Look for JSON anywhere in the text (multiple potential JSON blocks)
    const jsonMatches = jsonPayload.match(/\[[\s\S]*?\]/g);
    if (jsonMatches) {
      for (const match of jsonMatches) {
        try {
          const parsed = JSON.parse(match);
          if (Array.isArray(parsed)) {
            const result = processValidUsecaseData(parsed);
            if (result && result.length > 0) {
              return result;
            }
          }
        } catch (matchParseError) {
          continue; // Try next match
        }
      }
    }

    // Strategy 4: Aggressive JSON preprocessing to handle common LLM formatting issues
    let processedPayload = jsonPayload;

    // Remove any leading/trailing non-JSON text
    const arrayStartIndex = processedPayload.indexOf("[");
    const arrayEndIndex = processedPayload.lastIndexOf("]");

    if (
      arrayStartIndex !== -1 &&
      arrayEndIndex !== -1 &&
      arrayEndIndex > arrayStartIndex
    ) {
      processedPayload = processedPayload.substring(
        arrayStartIndex,
        arrayEndIndex + 1
      );
    }

    // Step 1: Fix property name issues (spaces, quotes, etc.)
    processedPayload = processedPayload.replace(
      /"usecase\s+title"/gi,
      '"usecaseTitle"'
    );
    processedPayload = processedPayload.replace(
      /"usecase\s+keywords"/gi,
      '"usecaseKeywords"'
    );
    processedPayload = processedPayload.replace(
      /"usecase\s+description"/gi,
      '"usecaseDescription"'
    );

    // Handle other variations
    processedPayload = processedPayload.replace(
      /"\s*title\s*"/gi,
      '"usecaseTitle"'
    );
    processedPayload = processedPayload.replace(
      /"\s*keywords\s*"/gi,
      '"usecaseKeywords"'
    );
    processedPayload = processedPayload.replace(
      /"\s*description\s*"/gi,
      '"usecaseDescription"'
    );

    // Step 2: Convert arrays to strings inline in the JSON
    // This regex finds array values and converts them to strings
    processedPayload = processedPayload.replace(
      /("usecase(?:Title|Keywords|Description)"\s*:\s*)\[([\s\S]*?)\]/gi,
      (match, prefix, arrayContent) => {
        try {
          // Parse the array content
          const arrayMatch = `[${arrayContent}]`;
          const parsedArray = JSON.parse(arrayMatch);

          if (Array.isArray(parsedArray)) {
            let joinedString;
            if (prefix.includes("Keywords")) {
              // For keywords, join with commas
              joinedString = parsedArray
                .map((item) => String(item).replace(/"/g, ""))
                .join(", ");
            } else if (prefix.includes("Description")) {
              // For descriptions, join with bullet points
              joinedString = parsedArray
                .map((item) => `• ${String(item).replace(/"/g, "")}`)
                .join("\\n");
            } else {
              // For titles, just join with spaces
              joinedString = parsedArray
                .map((item) => String(item).replace(/"/g, ""))
                .join(" ");
            }
            return `${prefix}"${joinedString}"`;
          }
        } catch (e) {
          // If array parsing fails, try to extract strings manually
          const strings = arrayContent.match(/"([^"]*)"/g) || [];
          const cleanStrings = strings.map((s) => s.replace(/"/g, ""));

          let joinedString;
          if (prefix.includes("Keywords")) {
            joinedString = cleanStrings.join(", ");
          } else if (prefix.includes("Description")) {
            joinedString = cleanStrings.map((item) => `• ${item}`).join("\\n");
          } else {
            joinedString = cleanStrings.join(" ");
          }
          return `${prefix}"${joinedString}"`;
        }
        return match; // fallback to original if all else fails
      }
    );

    // Step 3: Clean up common JSON syntax issues
    processedPayload = processedPayload.replace(/,(\s*[}\]])/g, "$1"); // Remove trailing commas
    processedPayload = processedPayload.replace(/\n\s*\n/g, "\n"); // Remove empty lines
    processedPayload = processedPayload.replace(
      /\s*effected by[^}]*?(?=\s*[},\]])/gi,
      ""
    ); // Remove malformed text

    // Step 4: Ensure proper JSON structure
    if (!processedPayload.trim().startsWith("[")) {
      processedPayload = "[" + processedPayload;
    }
    if (!processedPayload.trim().endsWith("]")) {
      // Count and balance brackets
      const openBrackets = (processedPayload.match(/\[/g) || []).length;
      const closeBrackets = (processedPayload.match(/\]/g) || []).length;
      const openBraces = (processedPayload.match(/{/g) || []).length;
      const closeBraces = (processedPayload.match(/}/g) || []).length;

      // Close any open objects first
      if (openBraces > closeBraces) {
        processedPayload += "}";
      }
      // Then close the array
      if (openBrackets > closeBrackets) {
        processedPayload += "]";
      }
    }

    // Step 5: Try parsing the processed JSON
    try {
      const parsed = JSON.parse(processedPayload);
      if (Array.isArray(parsed)) {
        return processValidUsecaseData(parsed);
      }
    } catch (parseError) {
      logger.debug.warn(
        "Processed JSON still failed to parse for usecases, trying manual extraction"
      );
    }

    // Step 6: Manual extraction as final fallback
    return extractUsecasesManually(jsonPayload);
  } catch (error) {
    logger.error("Failed to parse LLM use-case features JSON:", error);
    logger.error("Raw response:", raw);

    // Final fallback: try manual extraction
    try {
      return extractUsecasesManually(raw);
    } catch (extractError) {
      logger.error("Manual extraction also failed:", extractError);
      return null;
    }
  }
};

/**
 * Manual extraction of usecase data when JSON parsing completely fails
 */
const extractUsecasesManually = (raw: string): UsecaseFeature[] | null => {
  const usecases: UsecaseFeature[] = [];

  // Try to extract individual objects using regex patterns
  const objectPattern =
    /\{\s*["']?(?:usecase\s*title|title)["']?\s*:\s*([^\n]*?)[\n,}]/gi;
  const matches = raw.matchAll(objectPattern);

  // If regex approach fails, try line-by-line extraction
  const lines = raw.split("\n");
  let currentUsecase: Partial<UsecaseFeature> = {};

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Look for title patterns
    const titleMatch = trimmedLine.match(
      /["']?(?:usecase\s*title|title)["']?\s*:\s*(.+)/i
    );
    if (titleMatch) {
      if (Object.keys(currentUsecase).length > 0) {
        // Save previous usecase if it has required fields
        if (
          currentUsecase.usecaseTitle &&
          currentUsecase.usecaseKeywords &&
          currentUsecase.usecaseDescription
        ) {
          usecases.push(currentUsecase as UsecaseFeature);
        }
        currentUsecase = {};
      }
      currentUsecase.usecaseTitle = cleanExtractedValue(titleMatch[1]);
    }

    // Look for keywords patterns
    const keywordsMatch = trimmedLine.match(
      /["']?(?:usecase\s*keywords|keywords)["']?\s*:\s*(.+)/i
    );
    if (keywordsMatch) {
      currentUsecase.usecaseKeywords = cleanExtractedValue(keywordsMatch[1]);
    }

    // Look for description patterns
    const descMatch = trimmedLine.match(
      /["']?(?:usecase\s*description|description)["']?\s*:\s*(.+)/i
    );
    if (descMatch) {
      currentUsecase.usecaseDescription = cleanExtractedValue(descMatch[1]);
    }
  }

  // Don't forget the last usecase
  if (
    currentUsecase.usecaseTitle &&
    currentUsecase.usecaseKeywords &&
    currentUsecase.usecaseDescription
  ) {
    usecases.push(currentUsecase as UsecaseFeature);
  }

  return usecases.length > 0 ? usecases : null;
};

/**
 * Helper to clean extracted values from manual parsing
 */
const cleanExtractedValue = (value: string): string => {
  let cleaned = value.trim();

  // Remove quotes and common JSON artifacts
  cleaned = cleaned.replace(/^["'\[]+|["'\],?]+$/g, "");

  // Handle arrays by joining them
  if (cleaned.includes('",')) {
    const parts = cleaned
      .split('",')
      .map((part) => part.replace(/"/g, "").trim());
    // Join with bullets for descriptions, commas for keywords
    if (
      cleaned.toLowerCase().includes("bullet") ||
      parts.some((p) => p.includes("•"))
    ) {
      cleaned = parts
        .map((part) => (part.startsWith("•") ? part : `• ${part}`))
        .join("\n");
    } else {
      cleaned = parts.join(", ");
    }
  }

  return cleaned;
};

// Helper function to process valid use-case data
const processValidUsecaseData = (parsed: any[]): UsecaseFeature[] | null => {
  // Validate & normalise each entry
  const normalised: UsecaseFeature[] = [];

  parsed.forEach((item: any, idx: number) => {
    if (!item || typeof item !== "object") return;

    // Extract values with extensive fallbacks for various property name formats
    let usecaseTitle =
      item.usecaseTitle ||
      item["usecase title"] ||
      item["usecase-title"] ||
      item.title ||
      item.usecaseTitle ||
      item.name ||
      item.feature ||
      item.usecase;

    let usecaseKeywords =
      item.usecaseKeywords ||
      item["usecase keywords"] ||
      item["usecase-keywords"] ||
      item.keywords ||
      item.tags ||
      item.key_words ||
      item.searchTerms;

    let usecaseDescription =
      item.usecaseDescription ||
      item["usecase description"] ||
      item["usecase-description"] ||
      item.description ||
      item.details ||
      item.summary ||
      item.explanation;

    // Robust conversion to strings
    if (usecaseTitle != null) {
      if (Array.isArray(usecaseTitle)) {
        usecaseTitle = usecaseTitle.map((t) => String(t)).join(" ");
      } else {
        usecaseTitle = String(usecaseTitle);
      }
    }

    if (usecaseKeywords != null) {
      if (Array.isArray(usecaseKeywords)) {
        // For keywords, join with commas for better separation
        usecaseKeywords = usecaseKeywords
          .map((k) =>
            String(k)
              .replace(/["\[\]]/g, "")
              .trim()
          )
          .join(", ");
      } else {
        usecaseKeywords = String(usecaseKeywords).replace(/["\[\]]/g, "");
      }
    }

    if (usecaseDescription != null) {
      if (Array.isArray(usecaseDescription)) {
        // For descriptions, join with bullet points for readability
        usecaseDescription = usecaseDescription
          .map((bullet: any) => {
            const bulletStr = String(bullet)
              .replace(/["\[\]]/g, "")
              .trim();
            return bulletStr.startsWith("•") ? bulletStr : `• ${bulletStr}`;
          })
          .join("\n");
      } else {
        usecaseDescription = String(usecaseDescription).replace(/["\[\]]/g, "");
        // If it looks like it contains multiple sentences/points, format with bullets
        if (
          usecaseDescription.includes(".") &&
          usecaseDescription.split(".").length > 2
        ) {
          const sentences = usecaseDescription
            .split(".")
            .filter((s) => s.trim().length > 0);
          if (sentences.length > 1) {
            usecaseDescription = sentences
              .map((s) => `• ${s.trim()}`)
              .join("\n");
          }
        }
      }
    }

    // Validate and ensure we have required data
    const hasTitle =
      usecaseTitle &&
      typeof usecaseTitle === "string" &&
      usecaseTitle.trim().length > 0;
    const hasKeywords =
      usecaseKeywords &&
      typeof usecaseKeywords === "string" &&
      usecaseKeywords.trim().length > 0;
    const hasDescription =
      usecaseDescription &&
      typeof usecaseDescription === "string" &&
      usecaseDescription.trim().length > 0;

    if (hasTitle && hasKeywords && hasDescription) {
      normalised.push({
        usecaseTitle: usecaseTitle.trim(),
        usecaseKeywords: usecaseKeywords.trim(),
        usecaseDescription: usecaseDescription.trim(),
      });
    } else {
      logger.debug.warn(
        `Discarding malformed use-case entry at index ${idx}. ` +
          `hasTitle: ${hasTitle}, hasKeywords: ${hasKeywords}, hasDescription: ${hasDescription}. ` +
          `Raw item:`,
        item
      );
    }
  });

  logger.debug.info(
    `Successfully processed ${normalised.length} usecase features from ${parsed.length} raw entries`
  );
  return normalised.length ? normalised : null;
};

/**
 * Fallback function that uses the original FlexSearch approach when smart extractor fails
 */
const getRelevantFilesFromKeywordsFallback = async (
  keywords: string,
  fileIndex: any,
  documentMap: Map<string, any>,
  repoData: any
): Promise<{
  repoDataObjects: Array<{
    path: string;
    content: string;
    fileName: string;
    type: string;
    isSnippet?: boolean;
    originalTokenCount?: number;
    snippetTokenCount?: number;
  }>;
  fileNames: string[];
}> => {
  // Parse keywords (split by comma and clean up)
  const keywordList = keywords
    .split(",")
    .map((keyword) => keyword.trim())
    .filter((keyword) => keyword.length > 0);

  if (keywordList.length === 0) {
    logger.debug.warn("No valid keywords provided to fallback function");
    return { repoDataObjects: [], fileNames: [] };
  }

  logger.debug.info(`Fallback search for keywords: ${keywordList.join(", ")}`);

  // Set to track unique file paths to avoid duplicates
  const uniqueFilePaths = new Set<string>();
  const repoDataObjects: Array<{
    path: string;
    content: string;
    fileName: string;
    type: string;
    isSnippet?: boolean;
    originalTokenCount?: number;
    snippetTokenCount?: number;
  }> = [];

  // Search for each keyword individually
  for (const keyword of keywordList) {
    try {
      // Use FlexSearch to find matching document IDs
      const resultIds = fileIndex.search(keyword, {
        exact: false, // Use fuzzy search for better matching
      });

      logger.debug.info(
        `Fallback: Found ${resultIds.length} results for keyword: "${keyword}"`
      );

      // Process each search result
      for (const id of resultIds) {
        try {
          // Get the document from the map
          const document = documentMap.get(id.toString());
          if (!document || !document.content || !document.path) {
            continue;
          }

          // Skip if we've already processed this file path
          if (uniqueFilePaths.has(document.path)) {
            continue;
          }

          // Add to unique paths set
          uniqueFilePaths.add(document.path);

          // Ensure we have the file name
          const fileName =
            document.fileName || document.path.split("/").pop() || "";

          // Check if file exceeds thresholds for snippet extraction
          const shouldUseSnippet =
            document.exceedsThresholds ||
            (document.metadata && document.metadata.exceedsThresholds);

          let contentToUse = document.content;
          let isSnippet = false;
          let originalTokenCount =
            document.tokenCount || document.metadata?.tokenCount;
          let snippetTokenCount = originalTokenCount;

          if (shouldUseSnippet) {
            // Extract snippet around search matches
            contentToUse = extractSnippetAroundMatches(
              document.content,
              keywordList // Pass all keywords to find matches
            );

            isSnippet = true;
            snippetTokenCount = estimateTokenCount(contentToUse);

            logger.debug.info(
              `Fallback: Using snippet for large file ${document.path}: ` +
                `${originalTokenCount} -> ${snippetTokenCount} tokens`
            );
          } else {
            logger.debug.info(
              `Fallback: Using full content for file ${document.path}: ${originalTokenCount} tokens`
            );
          }

          // Add to results
          const resultObject: {
            path: string;
            content: string;
            fileName: string;
            type: string;
            isSnippet?: boolean;
            originalTokenCount?: number;
            snippetTokenCount?: number;
          } = {
            path: document.path,
            content: contentToUse,
            fileName: fileName,
            type: document.type || "blob",
          };

          // Add optional fields for snippet information
          if (isSnippet) {
            resultObject.isSnippet = true;
            resultObject.originalTokenCount = originalTokenCount;
            resultObject.snippetTokenCount = snippetTokenCount;
          }

          repoDataObjects.push(resultObject);
        } catch (error) {
          logger.debug.warn(
            `Fallback: Error processing search result for keyword "${keyword}":`,
            error
          );
        }
      }
    } catch (error) {
      logger.error(
        `Fallback: Error searching for keyword "${keyword}":`,
        error
      );
    }
  }

  // Extract just the file names
  const fileNames = repoDataObjects.map((obj) => obj.fileName);

  const result = {
    repoDataObjects,
    fileNames,
  };

  logger.debug.info(
    `Fallback search completed: Found ${
      repoDataObjects.length
    } files for keywords: ${keywordList.join(", ")}`
  );

  return result;
};

/**
 * Searches the codebase for files containing the provided keywords and returns
 * aggregated, deduplicated results. Uses the SmartContextExtractor to perform
 * intelligent code extraction that focuses on relevant functions/classes instead
 * of naive line-based snippets.
 *
 * @param keywords - Comma-separated string of keywords to search for
 * @returns Object with two keys:
 *   - repoDataObjects: Array of repo data objects with intelligently extracted content
 *   - fileNames: Array of just the file names (not full paths)
 */
export const getRelevantFilesFromKeywords = async (
  keywords: string
): Promise<{
  repoDataObjects: Array<{
    path: string;
    content: string;
    fileName: string;
    type: string;
    isSnippet?: boolean;
    originalTokenCount?: number;
    snippetTokenCount?: number;
  }>;
  fileNames: string[];
}> => {
  const { fileIndex, documentMap, repoData } = useStore.getState();

  logger.debug.info(
    "DEBUGGING_AI_QUALITY: getRelevantFilesFromKeywords - Starting",
    {
      keywords: keywords,
      keywordLength: keywords.length,
      fileIndexExists: !!fileIndex,
      documentMapSize: documentMap?.size || 0,
      repoDataKeys: Object.keys(repoData || {}).length,
      timestamp: new Date().toISOString(),
    }
  );

  // Guard against search infrastructure not being ready
  if (!fileIndex || !documentMap || documentMap.size === 0) {
    logger.debug.info(
      "DEBUGGING_AI_QUALITY: getRelevantFilesFromKeywords - Infrastructure Not Ready",
      {
        fileIndexExists: !!fileIndex,
        documentMapSize: documentMap?.size || 0,
        timestamp: new Date().toISOString(),
      }
    );

    logger.debug.warn(
      "getRelevantFilesFromKeywords called before search infrastructure is ready"
    );
    return { repoDataObjects: [], fileNames: [] };
  }

  // Parse keywords (split by comma and clean up)
  const keywordList = keywords
    .split(",")
    .map((keyword) => keyword.trim())
    .filter((keyword) => keyword.length > 0);

  if (keywordList.length === 0) {
    logger.debug.info(
      "DEBUGGING_AI_QUALITY: getRelevantFilesFromKeywords - No Valid Keywords",
      {
        originalKeywords: keywords,
        timestamp: new Date().toISOString(),
      }
    );

    logger.debug.warn(
      "No valid keywords provided to getRelevantFilesFromKeywords"
    );
    return { repoDataObjects: [], fileNames: [] };
  }

  logger.debug.info(
    "DEBUGGING_AI_QUALITY: getRelevantFilesFromKeywords - Parsed Keywords",
    {
      originalKeywords: keywords,
      parsedKeywords: keywordList,
      keywordCount: keywordList.length,
      timestamp: new Date().toISOString(),
    }
  );

  logger.debug.info(
    `Starting smart search for keywords: ${keywordList.join(", ")}`
  );

  try {
    // Use the smart context extractor
    const extractedFiles = await smartContextExtractor.extractRelevantContext(
      keywordList,
      documentMap,
      fileIndex,
      repoData
    );

    // DEBUG: Log the final decision for monitoring
    logger.debug.info("extractedFiles", extractedFiles);

    // If smart extractor found no results, fall back to the original approach
    if (extractedFiles.length === 0) {
      logger.debug.info(
        "DEBUGGING_AI_QUALITY: getRelevantFilesFromKeywords - Smart Extractor Failed, Using Fallback",
        {
          keywordList: keywordList,
          timestamp: new Date().toISOString(),
        }
      );

      logger.debug.warn(
        "Smart extractor found no results, falling back to original approach"
      );

      // Fallback: Use the original FlexSearch approach
      const fallbackResults = await getRelevantFilesFromKeywordsFallback(
        keywords,
        fileIndex,
        documentMap,
        repoData
      );

      logger.debug.info(
        "DEBUGGING_AI_QUALITY: getRelevantFilesFromKeywords - Fallback Results",
        {
          filesFound: fallbackResults.repoDataObjects.length,
          filePaths: fallbackResults.repoDataObjects.map((f) => f.path),
          totalTokens: fallbackResults.repoDataObjects.reduce(
            (sum, f) =>
              sum + (f.snippetTokenCount || f.originalTokenCount || 0),
            0
          ),
          snippetFiles: fallbackResults.repoDataObjects.filter(
            (f) => f.isSnippet
          ).length,
          timestamp: new Date().toISOString(),
        }
      );

      logger.debug.info("Fallback results:", fallbackResults);
      return fallbackResults;
    }

    // Convert to the expected format
    const repoDataObjects = extractedFiles.map((file) => ({
      path: file.path,
      content: file.content,
      fileName: file.fileName,
      type: file.type,
      isSnippet: file.isSnippet,
      originalTokenCount: file.originalTokenCount,
      snippetTokenCount: file.snippetTokenCount,
    }));

    // Extract just the file names
    const fileNames = repoDataObjects.map((obj) => obj.fileName);

    const result = {
      repoDataObjects,
      fileNames,
    };

    // Calculate total tokens for debugging
    const totalTokens = repoDataObjects.reduce((sum, obj) => {
      return sum + (obj.snippetTokenCount || obj.originalTokenCount || 0);
    }, 0);

    logger.debug.info(
      "DEBUGGING_AI_QUALITY: getRelevantFilesFromKeywords - Smart Extractor Success",
      {
        filesFound: repoDataObjects.length,
        filePaths: repoDataObjects.map((f) => f.path),
        totalTokens: totalTokens,
        snippetFiles: repoDataObjects.filter((f) => f.isSnippet).length,
        fullFiles: repoDataObjects.filter((f) => !f.isSnippet).length,
        avgTokensPerFile:
          repoDataObjects.length > 0
            ? Math.round(totalTokens / repoDataObjects.length)
            : 0,
        tokensSavedBySnippets: repoDataObjects
          .filter((f) => f.isSnippet)
          .reduce(
            (sum, f) =>
              sum + (f.originalTokenCount || 0) - (f.snippetTokenCount || 0),
            0
          ),
        timestamp: new Date().toISOString(),
      }
    );

    // Console log the output as requested
    logger.debug.info("getRelevantFilesFromKeywords result:", result);

    logger.debug.info(
      `Smart search completed: Found ${
        repoDataObjects.length
      } files for keywords: ${keywordList.join(", ")}. ` +
        `Total tokens: ${totalTokens}. All files use smart extraction.`
    );

    return result;
  } catch (error) {
    logger.debug.info(
      "DEBUGGING_AI_QUALITY: getRelevantFilesFromKeywords - Error",
      {
        keywordList: keywordList,
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      }
    );

    logger.error("Error in smart keyword search:", error);

    // Fallback to empty result on error
    return { repoDataObjects: [], fileNames: [] };
  }
};

/**
 * Component that observes the Zustand store and, once all onboarding
 * prerequisites are met, builds a RepoTree object and stores it in the global
 * store under `repoTree`.
 *
 * Trigger conditions:
 * 1. `documentMap` is populated (not null / undefined)
 * 2. `isNewDiagramFileCreated` is true
 * 3. `indexingProgress.isIndexing` has transitioned to false
 *
 * NOTE: This component renders nothing – it exists purely for its side-effects.
 */
const OnboardingWorkflow: React.FC = () => {
  const {
    documentMap,
    isNewDiagramFileCreated,
    indexingProgress,
    repoData,
    repoTree,
    setRepoTree,
    selectedModel,
    triggerOnboardingWorkflow,
    setTriggerOnboardingWorkflow,
    setSelectedTab,
    simulations,
    onboardingWorkflowState,
    setOnboardingWorkflowState,
    resetOnboardingWorkflowState,
    autoRepoDiagramConfig,
  } = useStore((state) => ({
    documentMap: state.documentMap,
    isNewDiagramFileCreated: state.isNewDiagramFileCreated,
    indexingProgress: state.indexingProgress,
    repoData: state.repoData,
    repoTree: state.repoTree,
    setRepoTree: state.setRepoTree,
    selectedModel: state.selectedModel,
    triggerOnboardingWorkflow: state.triggerOnboardingWorkflow,
    setTriggerOnboardingWorkflow: state.setTriggerOnboardingWorkflow,
    setSelectedTab: state.setSelectedTab,
    simulations: state.simulationsState.simulations,
    onboardingWorkflowState: state.onboardingWorkflowState,
    setOnboardingWorkflowState: state.setOnboardingWorkflowState,
    resetOnboardingWorkflowState: state.resetOnboardingWorkflowState,
    autoRepoDiagramConfig: state.autoRepoDiagramConfig,
  }));

  // state for readme files
  const [readmeFiles, setReadmeFiles] = useState<ReadmeFile[]>([]);
  // Track whether README file retrieval has been attempted (regardless of results)
  const [readmeRetrievalCompleted, setReadmeRetrievalCompleted] =
    useState(false);
  // Holds the LLM-suggested list of important files (for future UI rendering)
  const [suggestedFiles, setSuggestedFiles] = useState<SuggestedFile[] | null>(
    null
  );
  // Ensures the workflow runs only once per session
  const hasTriggeredRef = useRef(false);
  const llmTriggeredRef = useRef(false);
  const [usecaseFeatures, setUsecaseFeatures] = useState<
    UsecaseFeature[] | null
  >(null);
  const usecaseLLMTriggeredRef = useRef(false);
  const autoSwitchedToAITabRef = useRef(false);

  // Reset workflow state when a new repo is loaded (when documentMap changes and indexing starts)
  useEffect(() => {
    if (
      documentMap &&
      indexingProgress.isIndexing &&
      !onboardingWorkflowState.step1.isStarted
    ) {
      logger.debug.info(
        "OnboardingWorkflow: New repository detected, resetting workflow state"
      );
      // Reset the workflow state for the new repository
      resetOnboardingWorkflowState?.();
      // Reset all refs for the new repository
      hasTriggeredRef.current = false;
      llmTriggeredRef.current = false;
      usecaseLLMTriggeredRef.current = false;
      autoSwitchedToAITabRef.current = false;
      // Clear local state
      setReadmeFiles([]);
      setReadmeRetrievalCompleted(false);
      setSuggestedFiles(null);
      setUsecaseFeatures(null);
      logger.debug.info("OnboardingWorkflow: Workflow state reset completed");
    }
  }, [
    documentMap,
    indexingProgress.isIndexing,
    onboardingWorkflowState.step1.isStarted,
    resetOnboardingWorkflowState,
  ]);

  // Reset workflow state when a new repo is loaded
  useEffect(() => {
    if (documentMap && indexingProgress.isIndexing) {
      // Step 1 starts when indexing begins
      if (!onboardingWorkflowState.step1.isStarted) {
        // Initialize AI workflow logger
        aiWorkflowLogger.initializeWorkflow(`onboarding_${Date.now()}`);

        logger.debug.info(
          "OnboardingWorkflow: Step 1 - Starting repository indexing phase"
        );
        setOnboardingWorkflowState({
          step1: {
            ...onboardingWorkflowState.step1,
            isStarted: true,
            startTime: Date.now(),
          },
        });
      }
    }
  }, [
    documentMap,
    indexingProgress.isIndexing,
    onboardingWorkflowState.step1.isStarted,
    setOnboardingWorkflowState,
  ]);

  // STEP 1: Repository tree generation and indexing completion
  useEffect(() => {
    logger.debug.info("OnboardingWorkflow: Step 1 - Checking prerequisites", {
      hasTriggeredRef: hasTriggeredRef.current,
      documentMapExists: !!documentMap,
      indexingInProgress: indexingProgress?.isIndexing,
      simulationsLoaded: !!simulations,
      step1Started: onboardingWorkflowState.step1.isStarted,
      step1Completed: onboardingWorkflowState.step1.isCompleted,
    });

    if (
      !hasTriggeredRef.current &&
      documentMap &&
      indexingProgress &&
      !indexingProgress.isIndexing &&
      simulations && // Wait for simulations to be loaded first
      onboardingWorkflowState.step1.isStarted &&
      !onboardingWorkflowState.step1.isCompleted
    ) {
      aiWorkflowLogger.logStep(
        "Step 1 - Starting repository tree generation",
        {
          documentMapSize: documentMap?.size || 0,
          indexingComplete: !indexingProgress.isIndexing,
          repoDataKeys: Object.keys(repoData || {}).length,
        },
        "step1"
      );

      logger.debug.info(
        "OnboardingWorkflow: Step 1 - All prerequisites met, building repository tree"
      );

      // Always build the repository tree on app load
      const tree = new RepoTreeType(repoData as Record<string, any>);
      setRepoTree(tree);
      hasTriggeredRef.current = true;

      aiWorkflowLogger.logStep(
        "Step 1 - Repository tree generated",
        {
          treeStructure: tree.printTree(true).substring(0, 500) + "...",
          totalNodes: Object.keys(repoData || {}).length,
        },
        "step1"
      );

      // Mark step 1 as completed
      const endTime = Date.now();
      const duration = endTime - onboardingWorkflowState.step1.startTime!;
      setOnboardingWorkflowState({
        step1: {
          ...onboardingWorkflowState.step1,
          isCompleted: true,
          endTime,
        },
      });

      aiWorkflowLogger.logStep(
        "Step 1 - Completed",
        {
          duration: `${duration}ms`,
          success: true,
        },
        "step1"
      );

      logger.debug.info(
        `OnboardingWorkflow: Step 1 - Repository tree generated and stored (completed in ${duration}ms)`
      );

      // Reset manual trigger flag if it was set via UI button
      if (triggerOnboardingWorkflow && setTriggerOnboardingWorkflow) {
        setTriggerOnboardingWorkflow(false);
        logger.debug.info(
          "OnboardingWorkflow: Step 1 - Manual trigger flag reset"
        );
      }
    }
  }, [
    documentMap,
    indexingProgress,
    repoData,
    setRepoTree,
    simulations,
    triggerOnboardingWorkflow,
    setTriggerOnboardingWorkflow,
    onboardingWorkflowState.step1,
    setOnboardingWorkflowState,
  ]);

  // After setting the repoTree, retrieve up to 4 README.md files (with content)
  useEffect(() => {
    const fetchReadmes = async () => {
      aiWorkflowLogger.logStep(
        "README files retrieval - Starting",
        {
          repoTreeExists: !!repoTree,
        },
        "step1"
      );

      logger.debug.info("OnboardingWorkflow: Starting README files retrieval");
      const files = await getTopXReadmeFiles(4);
      setReadmeFiles(files);
      setReadmeRetrievalCompleted(true);

      aiWorkflowLogger.logStep(
        "README files retrieval - Completed",
        {
          filesFound: files.length,
          filePaths: files.map((f) => f.path),
          totalContentLength: files.reduce(
            (sum, f) => sum + f.content.length,
            0
          ),
          averageContentLength:
            files.length > 0
              ? Math.round(
                  files.reduce((sum, f) => sum + f.content.length, 0) /
                    files.length
                )
              : 0,
        },
        "step1"
      );

      logger.debug.info(
        `OnboardingWorkflow: README files retrieval completed - found ${files.length} files`
      );
    };

    if (repoTree && !readmeRetrievalCompleted) {
      fetchReadmes();
    }
  }, [repoTree, readmeRetrievalCompleted]);

  // AUTO REPOSITORY DIAGRAM GENERATION: Trigger after Step 1 completes when new diagram is created
  useEffect(() => {
    const triggerAutoDiagramGeneration = async () => {
      if (
        !repoTree ||
        !isNewDiagramFileCreated ||
        !autoRepoDiagramConfig.enabled
      )
        return;

      // Check if diagram generation is needed (70% threshold)
      if (!needsRepositoryDiagramGeneration()) {
        logger.debug.info(
          "OnboardingWorkflow: Auto Diagram Generation - Skipping because less than 70% of root-level files need diagram connection"
        );
        return;
      }

      logger.debug.info(
        "OnboardingWorkflow: Auto Diagram Generation - Starting automatic repository diagram generation",
        {
          repoTreeExists: !!repoTree,
          isNewDiagramFileCreated,
          config: autoRepoDiagramConfig,
        }
      );

      try {
        // Use configuration from store
        const options: AutoRepoDiagramOptions = {
          maxDepth: autoRepoDiagramConfig.maxDepth,
          generateFoldersOnly: autoRepoDiagramConfig.generateFoldersOnly,
        };

        const success = await generateRepositoryDiagram(options);

        if (success) {
          logger.debug.info(
            "OnboardingWorkflow: Auto Diagram Generation - Repository diagram generated successfully"
          );
        } else {
          logger.debug.warn(
            "OnboardingWorkflow: Auto Diagram Generation - Repository diagram generation failed"
          );
        }
      } catch (error) {
        logger.debug.error(
          "OnboardingWorkflow: Auto Diagram Generation - Error during repository diagram generation:",
          error
        );
      }
    };

    // Trigger auto diagram generation only after Step 1 is completed and when it's a new diagram and enabled
    if (
      repoTree &&
      onboardingWorkflowState.step1.isCompleted &&
      isNewDiagramFileCreated &&
      autoRepoDiagramConfig.enabled
    ) {
      triggerAutoDiagramGeneration();
    }
  }, [
    repoTree,
    onboardingWorkflowState.step1.isCompleted,
    isNewDiagramFileCreated,
    autoRepoDiagramConfig,
  ]);

  // STEP 2: LLM request for suggested files - only start after step 1 is complete
  useEffect(() => {
    const triggerLLMRequest = async () => {
      if (!repoTree) return;

      logger.debug.info(
        "OnboardingWorkflow: Step 2 - Evaluating trigger conditions",
        {
          repoTreeExists: !!repoTree,
          readmeRetrievalCompleted,
          readmeFilesCount: readmeFiles.length,
          triggerOnboardingWorkflow,
          isNewDiagramFileCreated,
          simulationsCount: Object.keys(simulations).length,
        }
      );

      // Check if there are any existing simulations (AI-generated or manual)
      const hasExistingSimulations = Object.keys(simulations).length > 0;

      // Check if we should trigger the LLM workflow based on conditions
      const hasAIGeneratedSimulations = Object.values(simulations).some(
        (simulation) => simulation.isAIGenerated
      );

      const shouldTriggerLLM =
        triggerOnboardingWorkflow ||
        isNewDiagramFileCreated ||
        (!hasAIGeneratedSimulations && !hasExistingSimulations);

      logger.debug.info(
        "OnboardingWorkflow: Step 2 - Trigger evaluation result",
        {
          shouldTriggerLLM,
          triggerOnboardingWorkflow,
          isNewDiagramFileCreated,
          hasExistingSimulations,
          hasAIGeneratedSimulations,
        }
      );

      if (!shouldTriggerLLM) {
        logger.debug.info(
          "OnboardingWorkflow: Step 2 - Skipping LLM workflow - either manually triggered with existing simulations, or conditions not met for automatic triggering"
        );
        llmTriggeredRef.current = false;
        return;
      }

      // Start step 2
      if (!onboardingWorkflowState.step2.isStarted) {
        logger.debug.info(
          "OnboardingWorkflow: Step 2 - Starting LLM request for suggested files"
        );
        setOnboardingWorkflowState({
          step2: {
            ...onboardingWorkflowState.step2,
            isStarted: true,
            startTime: Date.now(),
          },
        });
      }

      try {
        const PROMPT = `You are a senior engineer, you are given this codebase where you have access to the repo tree and the readme, the goal is to understand what are the primary usecases/features for this application from a UX point of view (i.e. usecases that provide business values) and their mapping to the actual source code that implements these features/usecases.
    \nYour task is to return a list of file paths for the top 30 files that you will read in the followup prompt to help you achieve the goal above. the list should be sorted by importance of each file to achieving the goal above.
    \nIMPORTANT: The file paths you return MUST exactly match the paths shown in the repo tree below. Do not guess or construct paths - only use paths that appear in the repo tree.
    \nYour output format should be exactly and only the following: a sorted list of objects, each object has two keys: filePath (string), reasonForPrioritizing (string). Note: Note: testing files sometimes give you an idea of what codebase really does so you might want to have at around 10 of the 30 files being testing files if test files exists.`;

        // Attach repo tree (compact) and the README contents
        const repoTreeCompact = repoTree.printTree(true);

        const readmesSection =
          readmeFiles.length > 0
            ? readmeFiles.map((f) => `### ${f.path}\n${f.content}`).join("\n\n")
            : "No README files available";

        const userMessage = `${PROMPT}\n\n----- Repo Tree (compact) -----\n${repoTreeCompact}\n\n----- README Files -----\n${readmesSection}`;

        logger.debug.info(
          "OnboardingWorkflow: Step 2 - Constructed LLM prompt",
          {
            promptLength: userMessage.length,
            repoTreeCompactLength: repoTreeCompact.length,
            readmesSectionLength: readmesSection.length,
            selectedModel,
          }
        );

        aiWorkflowLogger.logStep(
          "Step 2 - LLM Request Input",
          {
            model: selectedModel,
            promptTokens: estimateTokenCount(userMessage),
            promptLength: userMessage.length,
            repoTreeLength: repoTreeCompact.length,
            readmeCount: readmeFiles.length,
            readmesTotalLength: readmesSection.length,
            hasGeminiConfig: selectedModel?.toLowerCase().includes("gemini"),
            promptPreview: userMessage.substring(0, 200) + "...",
            fullPrompt: userMessage,
          },
          "step2"
        );

        const messages = [
          {
            role: "user" as const,
            content: userMessage,
          },
        ];

        // Gemini structured-output config to force JSON array of objects.
        const geminiStructuredOutputConfig = {
          responseMimeType: "application/json",
          responseSchema: {
            type: "ARRAY",
            items: {
              type: "OBJECT",
              properties: {
                filePath: { type: "STRING" },
                reasonForPrioritizing: { type: "STRING" },
              },
              propertyOrdering: ["filePath", "reasonForPrioritizing"],
            },
          },
        } as const;

        const chatCompletionBody: any = {
          model: selectedModel,
          messages,
          chatHistory: [],
        };

        // Attach config only if using a Gemini model
        if (selectedModel?.toLowerCase().includes("gemini")) {
          chatCompletionBody.config = geminiStructuredOutputConfig;
          logger.debug.info(
            "OnboardingWorkflow: Step 2 - Using Gemini structured output config"
          );
        }

        logger.debug.info(
          "OnboardingWorkflow: Step 2 - Sending LLM request..."
        );
        const response = await getChatCompletion(chatCompletionBody);

        aiWorkflowLogger.logStep(
          "Step 2 - LLM Response Received",
          {
            responseType: typeof response,
            hasChoices: response && "choices" in response,
            hasGeminiResponse: response && "geminiResponse" in response,
            hasCandidates: response && "candidates" in response,
          },
          "step2"
        );

        logger.debug.info(
          "OnboardingWorkflow: Step 2 - LLM response received",
          {
            responseType: typeof response,
            hasChoices: response && "choices" in response,
            hasGeminiResponse: response && "geminiResponse" in response,
            hasCandidates: response && "candidates" in response,
          }
        );

        // Try to extract assistant content in a provider-agnostic manner
        let assistantContent: string | undefined;
        if (
          response &&
          "choices" in response &&
          response.choices?.[0]?.message?.content
        ) {
          assistantContent = response.choices[0].message.content;
          logger.debug.info(
            "OnboardingWorkflow: Step 2 - Extracted content from OpenAI-style response"
          );
        } else if (
          "geminiResponse" in response &&
          response.geminiResponse?.candidates?.[0]?.content?.parts?.[0]?.text
        ) {
          assistantContent =
            response.geminiResponse.candidates[0].content.parts[0].text;
          logger.debug.info(
            "OnboardingWorkflow: Step 2 - Extracted content from Gemini response"
          );
        } else if (
          "candidates" in response &&
          (response as any).candidates?.[0]?.content?.parts?.[0]?.text
        ) {
          assistantContent = (response as any).candidates[0].content.parts[0]
            .text;
          logger.debug.info(
            "OnboardingWorkflow: Step 2 - Extracted content from alternative Gemini response"
          );
        } else if (typeof response === "string") {
          assistantContent = response;
          logger.debug.info(
            "OnboardingWorkflow: Step 2 - Using string response directly"
          );
        }

        if (assistantContent) {
          aiWorkflowLogger.logStep(
            "Step 2 - Raw LLM Response",
            {
              contentLength: assistantContent.length,
              responseTokens: estimateTokenCount(assistantContent),
              contentPreview: assistantContent.substring(0, 300) + "...",
              fullResponse: assistantContent,
            },
            "step2"
          );

          logger.debug.info(
            "OnboardingWorkflow: Step 2 - LLM response content",
            {
              contentLength: assistantContent.length,
              contentPreview: assistantContent.substring(0, 200) + "...",
            }
          );

          const parsed = parseSuggestedFiles(assistantContent);
          if (parsed) {
            aiWorkflowLogger.logStep(
              "Step 2 - Parsed Suggested Files",
              {
                totalFilesSuggested: parsed.length,
                filePaths: parsed.map((f) => f.filePath),
                avgReasonLength: Math.round(
                  parsed.reduce(
                    (sum, f) => sum + f.reasonForPrioritizing.length,
                    0
                  ) / parsed.length
                ),
                parsingSuccess: true,
              },
              "step2"
            );

            setSuggestedFiles(parsed);
            useStore.getState().setOnboardingSuggestedFiles(parsed);

            // Mark step 2 as completed
            const endTime = Date.now();
            const duration = endTime - onboardingWorkflowState.step2.startTime!;
            setOnboardingWorkflowState({
              step2: {
                ...onboardingWorkflowState.step2,
                isCompleted: true,
                endTime,
              },
            });

            aiWorkflowLogger.logStep(
              "Step 2 - Completed Successfully",
              {
                duration: `${duration}ms`,
                filesSelected: parsed.length,
                success: true,
              },
              "step2"
            );

            logger.debug.info(
              `OnboardingWorkflow: Step 2 - Successfully parsed and stored ${parsed.length} suggested files (completed in ${duration}ms)`
            );
          } else {
            aiWorkflowLogger.logStep(
              "Step 2 - Parsing Failed",
              {
                rawResponse: assistantContent.substring(0, 500) + "...",
                parsingSuccess: false,
              },
              "step2"
            );

            // Parsing failed - set error notification and reset ref to allow retry
            const errorMessage =
              "Parsing response from AI failed, prompting AI model again.";
            useStore.getState().setErrorNotification(errorMessage);
            logger.error(
              "OnboardingWorkflow: Step 2 - Failed to parse LLM suggested files response:",
              assistantContent
            );

            // Reset the ref to allow retrying this step
            llmTriggeredRef.current = false;

            // Store raw response for diagnostics
            const rawSuggestedFiles = [
              {
                filePath: "__RAW__",
                reasonForPrioritizing: assistantContent,
              },
            ];
            setSuggestedFiles(rawSuggestedFiles);
            useStore.getState().setOnboardingSuggestedFiles(rawSuggestedFiles);
            return; // Exit early to trigger retry
          }
        } else {
          aiWorkflowLogger.logStep(
            "Step 2 - No Content Received",
            {
              response: response,
            },
            "step2"
          );

          // No content received - set error notification and reset ref to allow retry
          const errorMessage =
            "Parsing response from AI failed, prompting AI model again.";
          useStore.getState().setErrorNotification(errorMessage);
          logger.error(
            "OnboardingWorkflow: Step 2 - LLM suggested files response did not contain content"
          );

          // Reset the ref to allow retrying this step
          llmTriggeredRef.current = false;
        }
      } catch (error) {
        // Network or other error - set error notification and reset ref to allow retry
        const errorMessage =
          "Parsing response from AI failed, prompting AI model again.";
        useStore.getState().setErrorNotification(errorMessage);
        logger.error(
          "OnboardingWorkflow: Step 2 - Failed to get LLM suggested files list:",
          error
        );

        // Reset the ref to allow retrying this step
        llmTriggeredRef.current = false;
      }
    };

    if (
      !llmTriggeredRef.current &&
      repoTree &&
      readmeRetrievalCompleted && // Wait for README retrieval to complete (even if no files found)
      onboardingWorkflowState.step1.isCompleted && // Only start after step 1 is complete
      !onboardingWorkflowState.step2.isCompleted
    ) {
      llmTriggeredRef.current = true;
      triggerLLMRequest();
    }
  }, [
    repoTree,
    readmeRetrievalCompleted,
    selectedModel,
    simulations,
    triggerOnboardingWorkflow,
    isNewDiagramFileCreated,
    onboardingWorkflowState.step1.isCompleted,
    onboardingWorkflowState.step2,
    setOnboardingWorkflowState,
  ]);

  // STEP 3: Use-case features LLM request - only start after step 2 is complete
  useEffect(() => {
    const triggerUsecaseLLMRequest = async () => {
      if (!repoTree || !suggestedFiles) return;

      logger.debug.info(
        "OnboardingWorkflow: Step 3 - Evaluating trigger conditions",
        {
          repoTreeExists: !!repoTree,
          readmeRetrievalCompleted,
          readmeFilesCount: readmeFiles.length,
          suggestedFilesCount: suggestedFiles?.length || 0,
          triggerOnboardingWorkflow,
          isNewDiagramFileCreated,
          simulationsCount: Object.keys(simulations).length,
        }
      );

      // Check if there are any existing simulations (AI-generated or manual)
      const hasExistingSimulations = Object.keys(simulations).length > 0;

      // Check if we should trigger the LLM workflow based on conditions
      const hasAIGeneratedSimulations = Object.values(simulations).some(
        (simulation) => simulation.isAIGenerated
      );

      const shouldTriggerLLM =
        triggerOnboardingWorkflow ||
        (isNewDiagramFileCreated &&
          !hasAIGeneratedSimulations &&
          !hasExistingSimulations) ||
        // Also trigger if step 2 completed and we're in AI workflow mode
        (onboardingWorkflowState.step2.isCompleted &&
          onboardingWorkflowState.step2.isStarted &&
          !hasExistingSimulations);

      logger.debug.info(
        "OnboardingWorkflow: Step 3 - Trigger evaluation result",
        {
          shouldTriggerLLM,
          triggerOnboardingWorkflow,
          isNewDiagramFileCreated,
          hasExistingSimulations,
          hasAIGeneratedSimulations,
          step2Completed: onboardingWorkflowState.step2.isCompleted,
        }
      );

      if (!shouldTriggerLLM) {
        logger.debug.info(
          "OnboardingWorkflow: Step 3 - Skipping usecase LLM workflow - either existing simulations found, or conditions not met for automatic triggering"
        );
        return;
      }

      // Start step 3
      if (!onboardingWorkflowState.step3.isStarted) {
        logger.debug.info(
          "OnboardingWorkflow: Step 3 - Starting LLM request for usecase features"
        );
        setOnboardingWorkflowState({
          step3: {
            ...onboardingWorkflowState.step3,
            isStarted: true,
            startTime: Date.now(),
          },
        });
      }

      try {
        const PROMPT_TWO = `You are a senior engineer, you are given this codebase where you have access to the repo tree, the readmes, and top 30 most important files in the codebase. the goal is to understand what are the primary usecases/features for this application from a UX point of view (i.e. usecases that provide business values) and their mapping to the actual source code that implements these features/usecases.\n\nYour task is to return a list of these usecase/features ranked by what you think are the most essential usecases that drive the most business value of the application. Return at least 5 usecases but aim for 10. \n\nOutput: output format should be a sorted list of objects, each object has three keys:\n    - usecase title (1 liner about the usecase)\n    - usecase keywords (top 3 keywords to search/grep from the codebase on a code-level search to get all the relevant source code for that usecase)\n    - usecase description (around 2-5 bullets explaining the usecase/feature)`;

        // Repo tree (compact)
        const repoTreeCompact = repoTree.printTree(true);

        // README section (consistent with Step 2)
        const readmesSection =
          readmeFiles.length > 0
            ? readmeFiles.map((f) => `### ${f.path}\n${f.content}`).join("\n\n")
            : "No README files available";

        // -----------------------------------------------------------------
        // Retrieve suggested files from the global store with improved path resolution
        // -----------------------------------------------------------------
        const filesSectionParts: string[] = [];
        let successfulFileCount = 0;
        let skippedFileCount = 0;

        logger.debug.info(
          "DEBUGGING_AI_QUALITY: Step 3 - File Content Retrieval Starting",
          {
            suggestedFilesCount: Math.min(suggestedFiles.length, 30),
            totalAvailable: suggestedFiles.length,
            repoDataKeys: Object.keys(repoData || {}).length,
            timestamp: new Date().toISOString(),
          }
        );

        logger.debug.info(
          `OnboardingWorkflow: Step 3 - Processing ${Math.min(
            suggestedFiles.length,
            30
          )} suggested files from global store with improved path resolution`
        );

        for (const { filePath } of suggestedFiles.slice(0, 30)) {
          if (!filePath || typeof filePath !== "string") {
            logger.debug.warn(
              `OnboardingWorkflow: Step 3 - Invalid filePath: ${filePath}`
            );
            continue;
          }

          try {
            // Use the new path resolution function to find the correct path in repoData
            const resolvedPath = findFileInRepoData(filePath, repoData);

            if (!resolvedPath) {
              logger.debug.info(
                "DEBUGGING_AI_QUALITY: Step 3 - File Path Resolution Failed",
                {
                  originalPath: filePath,
                  resolvedPath: null,
                  timestamp: new Date().toISOString(),
                }
              );

              logger.debug.warn(
                `OnboardingWorkflow: Step 3 - Could not resolve suggested file path '${filePath}' in repository (tried multiple variations)`
              );
              skippedFileCount++;
              continue;
            }

            // Use getFileContent function which already handles document map checking
            const content = await getFileContent(resolvedPath);

            if (!content) {
              logger.debug.info(
                "DEBUGGING_AI_QUALITY: Step 3 - File Content Not Found",
                {
                  originalPath: filePath,
                  resolvedPath: resolvedPath,
                  timestamp: new Date().toISOString(),
                }
              );

              logger.debug.warn(
                `OnboardingWorkflow: Step 3 - No content available in store for resolved file '${resolvedPath}' (original: '${filePath}') - skipping`
              );
              skippedFileCount++;
              continue;
            }

            // Get file size metadata
            const tokenCount = estimateTokenCount(content);
            const shouldUseSnippet = exceedsThresholds(content);

            logger.debug.info(
              "DEBUGGING_AI_QUALITY: Step 3 - File Content Retrieved",
              {
                originalPath: filePath,
                resolvedPath: resolvedPath,
                contentLength: content.length,
                tokenCount: tokenCount,
                shouldUseSnippet: shouldUseSnippet,
                timestamp: new Date().toISOString(),
              }
            );

            let finalContent = content;
            if (shouldUseSnippet) {
              // For Step 3, we don't have specific search terms, so just truncate large files
              const maxLines = 200; // Reasonable limit for context
              const lines = content.split("\n");
              if (lines.length > maxLines) {
                finalContent =
                  lines.slice(0, maxLines).join("\n") +
                  "\n\n[... file truncated for size ...]";

                logger.debug.info(
                  "DEBUGGING_AI_QUALITY: Step 3 - File Content Truncated",
                  {
                    originalPath: filePath,
                    originalLines: lines.length,
                    truncatedLines: maxLines,
                    originalTokens: tokenCount,
                    newTokens: estimateTokenCount(finalContent),
                    timestamp: new Date().toISOString(),
                  }
                );
              }
            }

            // Store the file content
            filesSectionParts.push(`### ${resolvedPath}\n${finalContent}`);
            successfulFileCount++;
          } catch (error) {
            logger.debug.info(
              "DEBUGGING_AI_QUALITY: Step 3 - File Processing Error",
              {
                filePath: filePath,
                error: error.message,
                timestamp: new Date().toISOString(),
              }
            );

            logger.debug.warn(
              `OnboardingWorkflow: Step 3 - Error processing file '${filePath}':`,
              error
            );
            skippedFileCount++;
          }
        }

        logger.debug.info(
          "DEBUGGING_AI_QUALITY: Step 3 - File Content Retrieval Summary",
          {
            totalRequested: Math.min(suggestedFiles.length, 30),
            successfulFiles: successfulFileCount,
            skippedFiles: skippedFileCount,
            successRate: `${Math.round(
              (successfulFileCount / Math.min(suggestedFiles.length, 30)) * 100
            )}%`,
            totalContentTokens: estimateTokenCount(
              filesSectionParts.join("\n\n")
            ),
            timestamp: new Date().toISOString(),
          }
        );

        // Construct the full user message
        const filesSectionString = filesSectionParts.join("\n\n");
        const userMessage = `${PROMPT_TWO}\n\n----- Repo Tree (compact) -----\n${repoTreeCompact}\n\n----- README Files -----\n${readmesSection}\n\n----- Top Files (content) -----\n${filesSectionString}`;

        // Calculate total tokens for the request
        const totalTokens = estimateTokenCount(userMessage);

        aiWorkflowLogger.logStep(
          "Step 3 - LLM Request Input",
          {
            model: selectedModel,
            totalPromptTokens: totalTokens,
            promptSections: {
              mainPrompt: estimateTokenCount(PROMPT_TWO),
              repoTree: estimateTokenCount(repoTreeCompact),
              readmes: estimateTokenCount(readmesSection),
              fileContents: estimateTokenCount(filesSectionString),
            },
            filesIncluded: successfulFileCount,
            maxTokenLimit: FILE_SIZE_THRESHOLDS.MAX_API_TOKENS,
            withinLimit: totalTokens <= FILE_SIZE_THRESHOLDS.MAX_API_TOKENS,
            fullPrompt: userMessage,
          },
          "step3"
        );

        logger.debug.info(
          `OnboardingWorkflow: Step 3 - Constructed LLM prompt with ${successfulFileCount} files (${totalTokens} tokens)`
        );

        const messages = [
          {
            role: "user" as const,
            content: userMessage,
          },
        ];

        // Structured output config to enforce JSON array of objects
        const structuredOutputConfig = {
          responseMimeType: "application/json",
          responseSchema: {
            type: "ARRAY",
            items: {
              type: "OBJECT",
              properties: {
                usecaseTitle: { type: "STRING" },
                usecaseKeywords: { type: "STRING" },
                usecaseDescription: { type: "STRING" },
              },
              propertyOrdering: [
                "usecaseTitle",
                "usecaseKeywords",
                "usecaseDescription",
              ],
            },
          },
        } as const;

        const chatCompletionBody: any = {
          model: selectedModel,
          messages,
          chatHistory: [],
        };

        if (selectedModel?.toLowerCase().includes("gemini")) {
          chatCompletionBody.config = structuredOutputConfig;
          logger.debug.info(
            "OnboardingWorkflow: Step 3 - Using Gemini structured output config"
          );
        }

        logger.debug.info(
          "OnboardingWorkflow: Step 3 - Sending LLM request..."
        );
        const response = await getChatCompletion(chatCompletionBody);

        // Mark that we've successfully made the first LLM call
        logger.debug.info(
          "OnboardingWorkflow: Step 3 - LLM response received",
          {
            responseType: typeof response,
            hasChoices: response && "choices" in response,
            hasGeminiResponse: response && "geminiResponse" in response,
            hasCandidates: response && "candidates" in response,
          }
        );

        // Extract assistant content similar to earlier helper
        let assistantContent: string | undefined;
        if (
          response &&
          "choices" in response &&
          (response as any).choices?.[0]?.message?.content
        ) {
          assistantContent = (response as any).choices[0].message.content;
          logger.debug.info(
            "OnboardingWorkflow: Step 3 - Extracted content from OpenAI-style response"
          );
        } else if (
          "geminiResponse" in response &&
          (response as any).geminiResponse?.candidates?.[0]?.content?.parts?.[0]
            ?.text
        ) {
          assistantContent = (response as any).geminiResponse.candidates[0]
            .content.parts[0].text;
          logger.debug.info(
            "OnboardingWorkflow: Step 3 - Extracted content from Gemini response"
          );
        } else if (
          "candidates" in response &&
          (response as any).candidates?.[0]?.content?.parts?.[0]?.text
        ) {
          assistantContent = (response as any).candidates[0].content.parts[0]
            .text;
          logger.debug.info(
            "OnboardingWorkflow: Step 3 - Extracted content from alternative Gemini response"
          );
        } else if (typeof response === "string") {
          assistantContent = response;
          logger.debug.info(
            "OnboardingWorkflow: Step 3 - Using string response directly"
          );
        }

        if (assistantContent) {
          aiWorkflowLogger.logStep(
            "Step 3 - Raw LLM Response",
            {
              contentLength: assistantContent.length,
              responseTokens: estimateTokenCount(assistantContent),
              contentPreview: assistantContent.substring(0, 300) + "...",
              fullResponse: assistantContent,
            },
            "step3"
          );

          logger.debug.info(
            "OnboardingWorkflow: Step 3 - LLM response content",
            {
              contentLength: assistantContent.length,
              contentPreview: assistantContent.substring(0, 200) + "...",
            }
          );

          const parsed = parseUsecaseFeatures(assistantContent);
          if (parsed) {
            logger.debug.info(
              "DEBUGGING_AI_QUALITY: Step 3 - Parsed Use Cases",
              {
                totalUseCases: parsed.length,
                useCaseTitles: parsed.map((uc) => uc.usecaseTitle),
                keywords: parsed.map((uc) => uc.usecaseKeywords),
                avgDescriptionLength: Math.round(
                  parsed.reduce(
                    (sum, uc) => sum + uc.usecaseDescription.length,
                    0
                  ) / parsed.length
                ),
                parsingSuccess: true,
                timestamp: new Date().toISOString(),
              }
            );

            setUsecaseFeatures(parsed);
            await useStore.getState().setUsecaseFeatures(parsed);

            // Mark step 3 as completed
            const endTime = Date.now();
            const duration = endTime - onboardingWorkflowState.step3.startTime!;
            setOnboardingWorkflowState({
              step3: {
                ...onboardingWorkflowState.step3,
                isCompleted: true,
                endTime,
              },
            });

            aiWorkflowLogger.logStep(
              "Step 3 - Completed Successfully",
              {
                duration: `${duration}ms`,
                useCasesGenerated: parsed.length,
                success: true,
              },
              "step3"
            );

            // Finalize the workflow and generate the report
            const report = aiWorkflowLogger.finalizeWorkflow();
            logger.debug.info(
              "OnboardingWorkflow: AI Quality Analysis Report Generated",
              { reportLength: report.length }
            );

            logger.debug.info(
              `OnboardingWorkflow: Step 3 - Parsed and stored LLM use-case features list (${parsed.length} usecases, completed in ${duration}ms)`
            );

            // Reset trigger flag as a fallback to ensure simulations display properly
            if (triggerOnboardingWorkflow && setTriggerOnboardingWorkflow) {
              setTriggerOnboardingWorkflow(false);
              logger.debug.info(
                "OnboardingWorkflow: Step 3 complete - resetting trigger flag (fallback)"
              );
            }
          } else {
            logger.debug.info("DEBUGGING_AI_QUALITY: Step 3 - Parsing Failed", {
              rawResponse: assistantContent.substring(0, 500) + "...",
              parsingSuccess: false,
              timestamp: new Date().toISOString(),
            });

            // Parsing failed - set error notification and reset ref to allow retry
            const errorMessage =
              "Parsing response from AI failed, prompting AI model again.";
            useStore.getState().setErrorNotification(errorMessage);
            logger.error(
              "OnboardingWorkflow: Step 3 - Failed to parse LLM use-case features response:",
              assistantContent
            );

            // Reset the ref to allow retrying this step
            usecaseLLMTriggeredRef.current = false;

            // Store raw for diagnostics
            const rawUsecaseFeatures = [
              {
                usecaseTitle: "__RAW__",
                usecaseKeywords: "__RAW__",
                usecaseDescription: assistantContent,
              },
            ];
            setUsecaseFeatures(rawUsecaseFeatures);
            await useStore.getState().setUsecaseFeatures(rawUsecaseFeatures);
            return; // Exit early to trigger retry
          }
        } else {
          logger.debug.info(
            "DEBUGGING_AI_QUALITY: Step 3 - No Content Received",
            {
              response: response,
              timestamp: new Date().toISOString(),
            }
          );

          // No content received - set error notification and reset ref to allow retry
          const errorMessage =
            "Parsing response from AI failed, prompting AI model again.";
          useStore.getState().setErrorNotification(errorMessage);
          logger.error(
            "OnboardingWorkflow: Step 3 - LLM use-case response did not contain content"
          );

          // Reset the ref to allow retrying this step
          usecaseLLMTriggeredRef.current = false;
        }
      } catch (error) {
        // Network or other error - set error notification and reset ref to allow retry
        const errorMessage =
          "Parsing response from AI failed, prompting AI model again.";
        useStore.getState().setErrorNotification(errorMessage);
        logger.error(
          "OnboardingWorkflow: Step 3 - Failed to get LLM use-case features list:",
          error
        );

        // Reset the ref to allow retrying this step
        usecaseLLMTriggeredRef.current = false;
      }
    };

    if (
      !usecaseLLMTriggeredRef.current &&
      repoTree &&
      readmeRetrievalCompleted && // Wait for README retrieval to complete (even if no files found)
      suggestedFiles &&
      onboardingWorkflowState.step2.isCompleted && // Only start after step 2 is complete
      !onboardingWorkflowState.step3.isCompleted
    ) {
      usecaseLLMTriggeredRef.current = true;
      triggerUsecaseLLMRequest();
    }
  }, [
    repoTree,
    readmeRetrievalCompleted,
    suggestedFiles,
    selectedModel,
    simulations,
    triggerOnboardingWorkflow,
    isNewDiagramFileCreated,
    onboardingWorkflowState.step2.isCompleted,
    onboardingWorkflowState.step3,
    setOnboardingWorkflowState,
    repoData,
  ]);

  // Auto-switch to Simulations tab when usecaseFeatures are populated and step 3 is complete
  useEffect(() => {
    if (
      usecaseFeatures &&
      usecaseFeatures.length > 0 &&
      onboardingWorkflowState.step3.isCompleted &&
      !autoSwitchedToAITabRef.current
    ) {
      autoSwitchedToAITabRef.current = true;

      logger.debug.info(
        `OnboardingWorkflow: Auto-switching to Simulations tab (${usecaseFeatures.length} usecases available)`
      );

      // Reset the trigger flag since the workflow is now complete
      if (triggerOnboardingWorkflow && setTriggerOnboardingWorkflow) {
        setTriggerOnboardingWorkflow(false);
        logger.debug.info(
          "OnboardingWorkflow: Workflow complete - resetting trigger flag"
        );
      }

      // Add a small delay to ensure all state updates are processed before switching tabs
      setTimeout(() => {
        // Switch to Simulations tab (index 3) since usecases are now natively integrated there
        setSelectedTab(3);
        logger.debug.info(
          "OnboardingWorkflow: Successfully switched to Simulations tab"
        );
      }, 100);
    }
  }, [
    usecaseFeatures,
    onboardingWorkflowState.step3.isCompleted,
    triggerOnboardingWorkflow,
    setTriggerOnboardingWorkflow,
    setSelectedTab,
  ]);

  return null;
};

export default OnboardingWorkflow;

/**
 * Generates a custom usecase based on user input. Uses LLM to analyze the user's
 * description and generate appropriate keywords (if not provided) and a formatted
 * description suitable for creating a simulation.
 *
 * @param userDescription - The user's description of the use case they want to understand
 * @param userKeywords - Optional keywords provided by the user (comma-separated)
 * @param repoTree - The repository tree structure
 * @param readmeFiles - Array of README files with content
 * @returns A UsecaseFeature object or null if generation fails
 */
export const generateCustomUsecase = async (
  userDescription: string,
  userKeywords: string,
  repoTree: any,
  readmeFiles: ReadmeFile[]
): Promise<UsecaseFeature | null> => {
  const { selectedModel } = useStore.getState();

  if (!selectedModel) {
    logger.error("No model selected for generateCustomUsecase");
    return null;
  }

  if (!userDescription?.trim()) {
    logger.warn("No description provided for custom usecase");
    return null;
  }

  try {
    const PROMPT = `You are a senior engineer analyzing a codebase. A user wants to understand a specific use case/feature and has provided the following description:

"${userDescription}"

${
  userKeywords
    ? `The user has also provided these keywords: ${userKeywords}`
    : "The user has not provided any keywords."
}

Based on the repository structure and README files below, please generate:

1. A clear, concise title for this use case (1 line)
2. Keywords for searching the codebase (if user provided keywords, include them and add more if needed; if not, generate 3-5 relevant keywords)
3. A well-structured description (2-5 bullet points) that explains what this use case/feature does

Your output should help identify the relevant code files and understand how this feature is implemented.`;

    // Build repo tree section
    const repoTreeCompact = repoTree?.printTree
      ? repoTree.printTree(true)
      : "Repository tree not available";

    // Build README section
    const readmesSection =
      readmeFiles.length > 0
        ? readmeFiles.map((f) => `### ${f.path}\n${f.content}`).join("\n\n")
        : "No README files available";

    // Construct the full user message
    const userMessage = `${PROMPT}

----- Repo Tree (compact) -----
${repoTreeCompact}

----- README Files -----
${readmesSection}`;

    aiWorkflowLogger.logStep(
      "generateCustomUsecase - LLM Request Input",
      {
        userDescription: userDescription,
        userKeywords: userKeywords,
        model: selectedModel,
        promptTokens: estimateTokenCount(userMessage),
        promptLength: userMessage.length,
        repoTreeLength: repoTreeCompact.length,
        readmeCount: readmeFiles.length,
        fullPrompt: userMessage,
      },
      "custom_usecase"
    );

    logger.debug.info("generateCustomUsecase: Constructed LLM prompt", {
      promptLength: userMessage.length,
      hasUserKeywords: !!userKeywords,
      selectedModel,
    });

    const messages = [
      {
        role: "user" as const,
        content: userMessage,
      },
    ];

    // Structured output config to enforce single object format
    const structuredOutputConfig = {
      responseMimeType: "application/json",
      responseSchema: {
        type: "OBJECT",
        properties: {
          usecaseTitle: { type: "STRING" },
          usecaseKeywords: { type: "STRING" },
          usecaseDescription: { type: "STRING" },
        },
        propertyOrdering: [
          "usecaseTitle",
          "usecaseKeywords",
          "usecaseDescription",
        ],
      },
    } as const;

    const chatCompletionBody: any = {
      model: selectedModel,
      messages,
      chatHistory: [],
    };

    if (selectedModel?.toLowerCase().includes("gemini")) {
      chatCompletionBody.config = structuredOutputConfig;
      logger.debug.info(
        "generateCustomUsecase: Using Gemini structured output config"
      );
    }

    logger.debug.info("generateCustomUsecase: Sending LLM request...");
    const response = await getChatCompletion(chatCompletionBody);

    // Extract assistant content
    let assistantContent: string | undefined;
    if (
      response &&
      "choices" in response &&
      (response as any).choices?.[0]?.message?.content
    ) {
      assistantContent = (response as any).choices[0].message.content;
    } else if (
      "geminiResponse" in response &&
      (response as any).geminiResponse?.candidates?.[0]?.content?.parts?.[0]
        ?.text
    ) {
      assistantContent = (response as any).geminiResponse.candidates[0].content
        .parts[0].text;
    } else if (
      "candidates" in response &&
      (response as any).candidates?.[0]?.content?.parts?.[0]?.text
    ) {
      assistantContent = (response as any).candidates[0].content.parts[0].text;
    } else if (typeof response === "string") {
      assistantContent = response;
    }

    if (assistantContent) {
      aiWorkflowLogger.logStep(
        "generateCustomUsecase - LLM Response",
        {
          contentLength: assistantContent.length,
          responseTokens: estimateTokenCount(assistantContent),
          fullResponse: assistantContent,
        },
        "custom_usecase"
      );

      try {
        // Parse the response as a single object (not an array)
        const parsed = JSON.parse(assistantContent);

        if (
          parsed &&
          parsed.usecaseTitle &&
          parsed.usecaseKeywords &&
          parsed.usecaseDescription
        ) {
          // If user provided keywords, prepend them to the AI-generated ones
          let finalKeywords = parsed.usecaseKeywords;
          if (userKeywords?.trim()) {
            // Remove any duplicate keywords
            const userKeywordsList = userKeywords
              .split(",")
              .map((k) => k.trim())
              .filter((k) => k);
            const aiKeywordsList = parsed.usecaseKeywords
              .split(",")
              .map((k) => k.trim())
              .filter((k) => k);
            const allKeywords = [
              ...new Set([...userKeywordsList, ...aiKeywordsList]),
            ];
            finalKeywords = allKeywords.join(", ");
          }

          const usecaseFeature: UsecaseFeature = {
            usecaseTitle: parsed.usecaseTitle,
            usecaseKeywords: finalKeywords,
            usecaseDescription: parsed.usecaseDescription,
          };

          logger.debug.info(
            "generateCustomUsecase: Successfully generated custom usecase",
            {
              title: usecaseFeature.usecaseTitle,
              keywordsCount: usecaseFeature.usecaseKeywords.split(",").length,
            }
          );

          return usecaseFeature;
        }
      } catch (parseError) {
        logger.error(
          "generateCustomUsecase: Failed to parse LLM response",
          parseError
        );
      }
    }

    logger.error("generateCustomUsecase: No valid content in LLM response");
    return null;
  } catch (error) {
    logger.error(
      "generateCustomUsecase: Failed to generate custom usecase:",
      error
    );
    return null;
  }
};

/**
 * Generates a dataflow diagram explanation for how a usecase is implemented
 * in the codebase. Uses LLM to analyze relevant files and create chronological
 * execution steps with source code mappings.
 *
 * @param repoDataObjects - Array of repo data objects with file content from getRelevantFilesFromKeywords
 * @param usecaseFeature - The usecase feature object containing title, keywords, description
 * @param repoTree - The repository tree structure
 * @param readmeFiles - Array of README files with content
 * @returns Array of step objects representing the dataflow diagram
 */
export const getSimulationDiagram = async (
  repoDataObjects: Array<{
    path: string;
    content: string;
    fileName: string;
    type: string;
    isSnippet?: boolean;
    originalTokenCount?: number;
    snippetTokenCount?: number;
  }>,
  usecaseFeature: UsecaseFeature,
  repoTree: any,
  readmeFiles: ReadmeFile[]
): Promise<Array<{
  stepType: string;
  stepName: string;
  stepDescription: string;
  inputDataExample: string;
  outputDataExample: string;
  sourceCodeMapping: {
    filePath: string;
    startLine: string;
    endLine: string;
    relevantVariables: string[];
  };
}> | null> => {
  const { selectedModel } = useStore.getState();

  logger.debug.info("DEBUGGING_AI_QUALITY: Simulation Generation - Starting", {
    usecaseTitle: usecaseFeature.usecaseTitle,
    usecaseKeywords: usecaseFeature.usecaseKeywords,
    selectedModel: selectedModel,
    inputFilesCount: repoDataObjects.length,
    inputFiles: repoDataObjects.map((f) => ({
      path: f.path,
      isSnippet: f.isSnippet,
      originalTokens: f.originalTokenCount,
      snippetTokens: f.snippetTokenCount,
    })),
    readmeFilesCount: readmeFiles.length,
    timestamp: new Date().toISOString(),
  });

  if (!selectedModel) {
    logger.debug.info(
      "DEBUGGING_AI_QUALITY: Simulation Generation - No Model Selected",
      {
        timestamp: new Date().toISOString(),
      }
    );
    logger.error("No model selected for getSimulationDiagram");
    return null;
  }

  if (repoDataObjects.length === 0) {
    logger.debug.info(
      "DEBUGGING_AI_QUALITY: Simulation Generation - No Input Files",
      {
        usecaseKeywords: usecaseFeature.usecaseKeywords,
        timestamp: new Date().toISOString(),
      }
    );
    logger.warn("No relevant files provided to getSimulationDiagram");
    return null;
  }

  try {
    const PROMPT = `You are a senior engineer and a solution architect, you are given this codebase where you have access to

- the repo tree, 
- the readme, 
- a list of files relevant to a usecase I want you to explain, 
- The usecase description that I want you to explain.


The goal is to understand how is this usecase/feature is implemented in code under the hood, specifically, I want to understand the data flow of this usecase as if I set debug breakpoints at the right places and then created a list of chronological code execution steps (along the data passed in at each step)and MOST IMPORTANTLY an accurate mapping to the actual source code that implements these steps.

Your task is to create a list of objects that represent the chronological sequence of the code exectuion steps. the object has the following structure:

{
stepType: string, //"node" or "edge",
stepName: string, // name of the step that we will show as title
stepDescription: string, // description of what happens in this step.
inputDataExample: string, // Example JSON object representing typical data being passed INTO this step. For edges, this represents the data being transmitted.
outputDataExample: string, // Example JSON object representing typical data being passed OUT of this step. For edges, this should be the same as inputDataExample.
sourceCodeMapping: {
		filePath: string, // the file path where the source code block that this steps executes resides
		startLine: string, // THE EXACT AND CORRECT AND ACCURATE start line of the source code block that this executes reside in the file above.
		endLine: string, // THE EXACT AND CORRECT AND ACCURATE end line of the source code block that this executes reside in the file above.
		relevantVariables: string[] // a list of variable names and functions within the codeblock that this step executes.
	},

}

CRITICAL REQUIREMENT about startLine and endLine:
- You tend to link to a piece of code that is close to the local of the correct code block but sometimes your startLine and endLine are shifted down or up by a few lines. be sure to double check the code block and the startLine and endLine are correct. HOWEVER, if you couldn't find  a better matching code block, still link to the code block that is close to the local of the correct code block.

IMPORTANT DATA EXAMPLE REQUIREMENTS:
- inputDataExample and outputDataExample should be actual example JSON objects (not schemas)
- Provide realistic, concrete examples that show what the actual data looks like
- Use real-looking values like actual usernames, IDs, file paths, etc.
- For primitive values, use actual examples: "john_doe", 42, true, null
- For objects, include realistic property values: {"id": "user_123", "name": "John Doe", "email": "john@example.com"}
- For arrays: ["item1", "item2"] or [{"id": 1, "name": "First Item"}, {"id": 2, "name": "Second Item"}]
- If no specific data is passed, use: null or {}
- For function parameters, show example parameter values
- For return values, show example return data

EXAMPLES OF GOOD DATA EXAMPLES:
- Simple string: "user_12345"
- User object: {"id": "user_123", "name": "John Doe", "email": "john@example.com", "role": "admin"}
- API response: {"success": true, "data": [{"id": 1, "title": "Todo Item"}], "message": "Retrieved successfully"}
- Function call with params: {"endpoint": "/api/users", "payload": {"name": "John"}, "headers": {"Content-Type": "application/json"}}

CRITICAL REQUIREMENT - STEP TYPE ALTERNATION:
You MUST ensure that the steps in your response alternate between "node" and "edge" stepTypes. This means:
- If step 1 is a "node", then step 2 must be an "edge"
- If step 2 is an "edge", then step 3 must be a "node"
- If step 3 is a "node", then step 4 must be an "edge"
- And so on...

This alternation pattern is absolutely mandatory and must be followed throughout the entire sequence. The first step should typically be a "node" (code logic block), followed by an "edge" (data transmission), then another "node", and so on. Ensure your analysis of the usecase follows this alternating pattern naturally by identifying the logical flow where code execution blocks are connected by data transmissions.

CRITICAL REQUIREMENT - DATA FLOW CONNECTIONS:
- HOWEVER, if the usecase has multiple independent data flows (e.g., scheduled updates vs real-time webhooks), represent them as separate sequences without connecting them. Each flow should maintain its own logical progression.
    - This means ofcourse, that two flows shouldn't be connected by an edge. e.g. the end of a flow shouldn't be connected to the beginning of another flow.


CRITICAL FILE EXCLUSION REQUIREMENT:
DO NOT use test files or documentation files as source code references in your simulation steps:
- Exclude any file with extensions like .test.js, .test.ts, .spec.js, .spec.ts, test.py, _test.go, etc.
- Exclude documentation files with extensions like .md, .txt, .rst, .doc, etc.
- Exclude example or mock files typically found in __tests__, test/, tests/, spec/, examples/ directories
- IMPORTANT: These files can be used FOR YOU to understand the codebase but MUST NOT be part of the simulation steps.
- CRITICAL: All simulation steps must be connected to their source code and that source code should be the actual code that executes during runtime. This is why the extensions above are excluded. Ofcourse, all simulation steps MUST be connected to their source code.

STEP TYPE DEFINITIONS:
a stepType should be defined as node if the it's a code logic block.
a stepType should be an edge if it has data sent from a source code logic block to a destination codelogic block. 
in the case of the edge we the inputDataExample and outputDataExample should be the same since an edge represents a data transmission.

PREFERRED REQUIREMENT - SOURCE CODE MAPPING FOR EDGES:
- For EDGE steps, it is HIGHLY PREFERRED (but not strictly required) to provide sourceCodeMapping when possible. This helps users understand the specific code responsible for data transmission such as:
  - Function calls that pass data between components
  - API calls that send data over HTTP
  - Event dispatching that transmits data
  - Database queries that move data
  - Message passing between modules
  - Data transformation functions that convert data format during transmission
  - Import/export statements that enable data flow between files
  - Method calls that transfer data between classes
  - Variable assignments that pass data between scopes
- Add label to the beginning of each step for e.g.:
    - Config
    - API Call
    - Database Query
    - Event Dispatch
    - Data Transformation
    - and the what flow it belongs to if there are multiple flows.

If you can identify specific lines of code where the data transmission occurs, include them. If the edge represents conceptual data flow that doesn't map to specific lines, you may use the same sourceCodeMapping as the source node or a related file with a brief comment in the relevantVariables field explaining the conceptual nature.

Your output should be all what a junior software engineer needs to read to be able to understand the usecase and how it's implemented in code under the hood.

 IMPORTANT: Any source code reference paths you return MUST exactly match the paths shown in the repo tree below. Do not guess or construct paths - only use paths that appear in the repo tree.`;

    // Build repo tree section
    const repoTreeCompact = repoTree?.printTree
      ? repoTree.printTree(true)
      : "Repository tree not available";

    // Build README section
    const readmesSection =
      readmeFiles.length > 0
        ? readmeFiles.map((f) => `### ${f.path}\n${f.content}`).join("\n\n")
        : "No README files available";

    // Build relevant files section
    const relevantFilesSection = repoDataObjects
      .map((file) => {
        // Add a note if this is a snippet to help the LLM understand the context
        const snippetNote = file.isSnippet
          ? `\n[NOTE: This is a code snippet extracted around relevant matches, not the complete file. Original file had ${file.originalTokenCount} tokens, snippet has ${file.snippetTokenCount} tokens.]\n`
          : "";

        return `### ${file.path}${snippetNote}\n${file.content}`;
      })
      .join("\n\n");

    // Build usecase section
    const usecaseSection = `
**Usecase Title:** ${usecaseFeature.usecaseTitle}
**Keywords:** ${usecaseFeature.usecaseKeywords}
**Description:** ${usecaseFeature.usecaseDescription}
    `.trim();

    // Construct the full user message
    const userMessage = `${PROMPT}

----- Usecase Information -----
${usecaseSection}

----- Repo Tree (compact) -----
${repoTreeCompact}

----- README Files -----
${readmesSection}

----- Relevant Files (content) -----
${relevantFilesSection}`;

    // **FRONTEND GUARD**: Calculate total tokens before sending to API
    const contentSections = [
      PROMPT,
      usecaseSection,
      repoTreeCompact,
      readmesSection,
      relevantFilesSection,
    ];
    const totalTokens = calculateTotalTokens(contentSections);

    aiWorkflowLogger.logStep(
      "Simulation Generation - Request Input",
      {
        usecaseTitle: usecaseFeature.usecaseTitle,
        model: selectedModel,
        totalPromptTokens: totalTokens,
        maxTokenLimit: FILE_SIZE_THRESHOLDS.MAX_API_TOKENS,
        withinLimit: totalTokens <= FILE_SIZE_THRESHOLDS.MAX_API_TOKENS,
        promptSections: {
          mainPrompt: estimateTokenCount(PROMPT),
          usecaseInfo: estimateTokenCount(usecaseSection),
          repoTree: estimateTokenCount(repoTreeCompact),
          readmes: estimateTokenCount(readmesSection),
          relevantFiles: estimateTokenCount(relevantFilesSection),
        },
        filesIncluded: repoDataObjects.length,
        snippetFiles: repoDataObjects.filter((f) => f.isSnippet).length,
        totalSnippetTokensSaved: repoDataObjects
          .filter((f) => f.isSnippet)
          .reduce(
            (sum, f) =>
              sum + (f.originalTokenCount || 0) - (f.snippetTokenCount || 0),
            0
          ),
        fullPrompt: userMessage,
      },
      "simulation"
    );

    logger.debug.info(
      `getSimulationDiagram: Total tokens to be sent: ${totalTokens} ` +
        `(limit: ${FILE_SIZE_THRESHOLDS.MAX_API_TOKENS})`
    );

    if (totalTokens > FILE_SIZE_THRESHOLDS.MAX_API_TOKENS) {
      const errorMessage =
        `Request aborted: Token count (${totalTokens}) exceeds maximum limit ` +
        `(${FILE_SIZE_THRESHOLDS.MAX_API_TOKENS}). Please reduce the scope of your request.`;

      logger.debug.info(
        "DEBUGGING_AI_QUALITY: Simulation Generation - Token Limit Exceeded",
        {
          totalTokens: totalTokens,
          maxLimit: FILE_SIZE_THRESHOLDS.MAX_API_TOKENS,
          excess: totalTokens - FILE_SIZE_THRESHOLDS.MAX_API_TOKENS,
          timestamp: new Date().toISOString(),
        }
      );

      logger.error(errorMessage);
      useStore.getState().setErrorNotification(errorMessage);

      return null;
    }

    const messages = [
      {
        role: "user" as const,
        content: userMessage,
      },
    ];

    // Structured output config to enforce JSON array of step objects
    const structuredOutputConfig = {
      responseMimeType: "application/json",
      responseSchema: {
        type: "ARRAY",
        items: {
          type: "OBJECT",
          properties: {
            stepType: { type: "STRING" },
            stepName: { type: "STRING" },
            stepDescription: { type: "STRING" },
            inputDataExample: { type: "STRING" },
            outputDataExample: { type: "STRING" },
            sourceCodeMapping: {
              type: "OBJECT",
              properties: {
                filePath: { type: "STRING" },
                startLine: { type: "STRING" },
                endLine: { type: "STRING" },
                relevantVariables: {
                  type: "ARRAY",
                  items: { type: "STRING" },
                },
              },
              propertyOrdering: [
                "filePath",
                "startLine",
                "endLine",
                "relevantVariables",
              ],
            },
          },
          propertyOrdering: [
            "stepType",
            "stepName",
            "stepDescription",
            "inputDataExample",
            "outputDataExample",
            "sourceCodeMapping",
          ],
        },
      },
    } as const;

    const chatCompletionBody: any = {
      model: selectedModel,
      messages,
      chatHistory: [],
    };

    // Attach structured output config only for Gemini models
    if (selectedModel?.toLowerCase().includes("gemini")) {
      chatCompletionBody.config = structuredOutputConfig;
    }

    logger.debug.info(
      `Requesting simulation diagram for usecase: ${usecaseFeature.usecaseTitle} ` +
        `(${totalTokens} tokens)`
    );

    const response = await getChatCompletion(chatCompletionBody);

    logger.debug.info(
      "DEBUGGING_AI_QUALITY: Simulation Generation - LLM Response Received",
      {
        usecaseTitle: usecaseFeature.usecaseTitle,
        responseType: typeof response,
        hasChoices: response && "choices" in response,
        hasGeminiResponse: response && "geminiResponse" in response,
        hasCandidates: response && "candidates" in response,
        timestamp: new Date().toISOString(),
      }
    );

    // Extract assistant content in a provider-agnostic manner
    let assistantContent: string | undefined;
    if (
      response &&
      "choices" in response &&
      response.choices?.[0]?.message?.content
    ) {
      assistantContent = response.choices[0].message.content;
    } else if (
      "geminiResponse" in response &&
      response.geminiResponse?.candidates?.[0]?.content?.parts?.[0]?.text
    ) {
      assistantContent =
        response.geminiResponse.candidates[0].content.parts[0].text;
    } else if (
      "candidates" in response &&
      (response as any).candidates?.[0]?.content?.parts?.[0]?.text
    ) {
      assistantContent = (response as any).candidates[0].content.parts[0].text;
    } else if (typeof response === "string") {
      assistantContent = response;
    }

    if (!assistantContent) {
      logger.debug.info(
        "DEBUGGING_AI_QUALITY: Simulation Generation - No Content Received",
        {
          usecaseTitle: usecaseFeature.usecaseTitle,
          response: response,
          timestamp: new Date().toISOString(),
        }
      );
      logger.error("No content received from LLM for simulation diagram");
      return null;
    }

    aiWorkflowLogger.logStep(
      "Simulation Generation - Raw LLM Response",
      {
        usecaseTitle: usecaseFeature.usecaseTitle,
        contentLength: assistantContent.length,
        responseTokens: estimateTokenCount(assistantContent),
        contentPreview: assistantContent.substring(0, 400) + "...",
        fullResponse: assistantContent,
      },
      "simulation"
    );

    // Parse the response
    const parsed = parseSimulationDiagramResponse(assistantContent);

    if (parsed) {
      logger.debug.info(
        "DEBUGGING_AI_QUALITY: Simulation Generation - Parsing Success",
        {
          usecaseTitle: usecaseFeature.usecaseTitle,
          totalSteps: parsed.length,
          stepTypes: parsed.map((s) => s.stepType),
          nodeSteps: parsed.filter((s) => s.stepType === "node").length,
          edgeSteps: parsed.filter((s) => s.stepType === "edge").length,
          alternatingPattern: checkAlternatingPattern(parsed),
          sourceFilePaths: [
            ...new Set(parsed.map((s) => s.sourceCodeMapping.filePath)),
          ],
          avgLineRange: Math.round(
            parsed.reduce((sum, s) => {
              const startLine = parseInt(s.sourceCodeMapping.startLine);
              const endLine = parseInt(s.sourceCodeMapping.endLine);
              return sum + (endLine - startLine + 1);
            }, 0) / parsed.length
          ),
          avgVariablesPerStep: Math.round(
            parsed.reduce(
              (sum, s) => sum + s.sourceCodeMapping.relevantVariables.length,
              0
            ) / parsed.length
          ),
          timestamp: new Date().toISOString(),
        }
      );

      logger.debug.info(
        `Successfully generated simulation diagram with ${parsed.length} steps for usecase: ${usecaseFeature.usecaseTitle}`
      );
      logger.debug.info("getSimulationDiagram result:", parsed);
      return parsed;
    } else {
      logger.debug.info(
        "DEBUGGING_AI_QUALITY: Simulation Generation - Parsing Failed",
        {
          usecaseTitle: usecaseFeature.usecaseTitle,
          rawResponse: assistantContent.substring(0, 500) + "...",
          timestamp: new Date().toISOString(),
        }
      );
      logger.error("Failed to parse simulation diagram response");
      return null;
    }
  } catch (error) {
    logger.debug.info("DEBUGGING_AI_QUALITY: Simulation Generation - Error", {
      usecaseTitle: usecaseFeature.usecaseTitle,
      error: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
    });
    logger.error("Error generating simulation diagram:", error);
    return null;
  }
};

// Helper function to check if steps alternate between node and edge
const checkAlternatingPattern = (steps: any[]): boolean => {
  for (let i = 1; i < steps.length; i++) {
    if (steps[i].stepType === steps[i - 1].stepType) {
      return false;
    }
  }
  return true;
};

/**
 * Parses the LLM response for simulation diagram steps into the expected format
 */
const parseSimulationDiagramResponse = (
  raw: string
): Array<{
  stepType: string;
  stepName: string;
  stepDescription: string;
  inputDataExample: string;
  outputDataExample: string;
  sourceCodeMapping: {
    filePath: string;
    startLine: string;
    endLine: string;
    relevantVariables: string[];
  };
}> | null => {
  try {
    if (!raw) throw new Error("No data to parse");

    let jsonPayload = raw.trim();

    // If the model wrapped the JSON in ```json ... ```, extract the inner part.
    const codeBlockMatch = jsonPayload.match(
      /```(?:json)?[\s\r\n]*([\s\S]*?)```/i
    );
    if (codeBlockMatch) {
      jsonPayload = codeBlockMatch[1].trim();
    }

    // Attempt to parse the JSON
    const parsed = JSON.parse(jsonPayload);

    if (!Array.isArray(parsed)) {
      throw new Error("Response is not an array");
    }

    // Validate and normalize each step
    const normalizedSteps = parsed
      .map((step: any, index: number) => {
        if (!step || typeof step !== "object") {
          logger.debug.warn(`Skipping invalid step at index ${index}`);
          return null;
        }

        // Validate required fields
        const requiredFields = [
          "stepType",
          "stepName",
          "stepDescription",
          "inputDataExample",
          "outputDataExample",
          "sourceCodeMapping",
        ];

        for (const field of requiredFields) {
          if (!step[field]) {
            logger.debug.warn(
              `Step at index ${index} missing required field: ${field}`
            );
            return null;
          }
        }

        // Validate sourceCodeMapping structure
        const mapping = step.sourceCodeMapping;
        if (!mapping.filePath || !mapping.startLine || !mapping.endLine) {
          logger.debug.warn(
            `Step at index ${index} has invalid sourceCodeMapping`
          );
          return null;
        }

        // Ensure relevantVariables is an array
        if (!Array.isArray(mapping.relevantVariables)) {
          mapping.relevantVariables = [];
        }

        return {
          stepType: String(step.stepType),
          stepName: String(step.stepName),
          stepDescription: String(step.stepDescription),
          inputDataExample: String(step.inputDataExample),
          outputDataExample: String(step.outputDataExample),
          sourceCodeMapping: {
            filePath: String(mapping.filePath),
            startLine: String(mapping.startLine),
            endLine: String(mapping.endLine),
            relevantVariables: mapping.relevantVariables.map((v: any) =>
              String(v)
            ),
          },
        };
      })
      .filter(Boolean); // Remove null entries

    return normalizedSteps.length > 0 ? normalizedSteps : null;
  } catch (error) {
    logger.error("Failed to parse simulation diagram response:", error);
    logger.error("Raw response:", raw);
    return null;
  }
};

/**
 * Complete workflow example: How to use both functions together when a usecase is selected
 *
 * Usage example:
 * ```typescript
 * import { getRelevantFilesFromKeywords, getSimulationDiagram } from './OnboardingWorkflow';
 *
 * const handleUsecaseSelection = async (selectedUsecase: UsecaseFeature) => {
 *   try {
 *     // Step 1: Get relevant files for the usecase keywords
 *     console.log(`Searching for files related to: ${selectedUsecase.usecaseKeywords}`);
 *     const relevantFiles = await getRelevantFilesFromKeywords(selectedUsecase.usecaseKeywords);
 *
 *     if (relevantFiles.repoDataObjects.length === 0) {
 *       console.log('No relevant files found for this usecase');
 *       return;
 *     }
 *
 *     console.log(`Found ${relevantFiles.repoDataObjects.length} relevant files:`, relevantFiles.fileNames);
 *
 *     // Step 2: Generate simulation diagram using the relevant files
 *     console.log(`Generating dataflow diagram for usecase: ${selectedUsecase.usecaseTitle}`);
 *
 *     // Get repoTree and readmeFiles from store (these should be available from onboarding workflow)
 *     const { repoTree } = useStore.getState();
 *     const readmeFiles = await getTopXReadmeFiles(4); // Reuse the existing function
 *
 *     const simulationSteps = await getSimulationDiagram(
 *       relevantFiles.repoDataObjects,
 *       selectedUsecase,
 *       repoTree,
 *       readmeFiles
 *     );
 *
 *     if (simulationSteps && simulationSteps.length > 0) {
 *       console.log(`Successfully generated ${simulationSteps.length} execution steps for the usecase`);
 *       console.log('Simulation steps:', simulationSteps);
 *
 *       // Now you can use the simulation steps to:
 *       // - Render a dataflow diagram
 *       // - Show step-by-step execution flow
 *       // - Highlight relevant source code sections
 *       // - Display data schemas for each step
 *
 *       return {
 *         usecase: selectedUsecase,
 *         relevantFiles: relevantFiles,
 *         simulationSteps: simulationSteps
 *       };
 *     } else {
 *       console.log('Failed to generate simulation diagram');
 *       return null;
 *     }
 *
 *   } catch (error) {
 *     console.error('Error in usecase analysis workflow:', error);
 *     return null;
 *   }
 * };
 */

/**
 * Attempts to find a file in repoData by trying multiple path variations.
 * This handles edge cases where folder names contain forward slashes (e.g., "/client")
 * and normalizes paths to match what's actually stored in repoData.
 *
 * @param filePath - The suggested file path from LLM
 * @param repoData - The repoData object to search in
 * @returns The normalized path that exists in repoData, or null if not found
 */
export const findFileInRepoData = (
  filePath: string,
  repoData: any
): string | null => {
  if (!filePath || !repoData) return null;

  // List of path variations to try, in order of preference
  const pathVariations = [
    filePath, // Try the original path first
    `/${filePath}`, // Try with leading slash
    filePath.replace(/^\/+/, ""), // Try without leading slash
  ];

  // Generate more sophisticated variations for paths with multiple segments
  const segments = filePath.split("/").filter(Boolean); // Remove empty segments
  if (segments.length > 1) {
    // Try variations where different segments have leading slashes
    const generatePathVariations = (segmentArray: string[]): string[] => {
      const variations: string[] = [];

      // Generate all possible combinations of segments with/without leading slashes
      const totalCombinations = Math.pow(2, segmentArray.length);

      for (let i = 0; i < totalCombinations; i++) {
        const variation = segmentArray.map((segment, index) => {
          const shouldAddSlash = (i >> index) & 1; // Binary bit check
          return shouldAddSlash ? `/${segment}` : segment;
        });

        // Join the segments and clean up multiple slashes
        const joinedPath = variation.join("/").replace(/\/+/g, "/");
        variations.push(joinedPath);

        // Also try with leading slash for the entire path
        if (!joinedPath.startsWith("/")) {
          variations.push(`/${joinedPath}`);
        }
      }

      return variations;
    };

    const additionalVariations = generatePathVariations(segments);
    pathVariations.push(...additionalVariations);

    // Try some common patterns specifically
    // Pattern: /client/src/... where client folder name is "/client"
    pathVariations.push(`/${segments[0]}/${segments.slice(1).join("/")}`);

    // Pattern: Multiple segments with slashes: /client/src/components -> (/client)/(/src)/components
    if (segments.length > 2) {
      const multiSlashPattern = segments
        .map((segment, index) => (index < 2 ? `/${segment}` : segment))
        .join("/");
      pathVariations.push(multiSlashPattern);
    }
  }

  // Remove duplicates while preserving order
  const uniqueVariations = [...new Set(pathVariations)];

  logger.debug.info(
    `OnboardingWorkflow: Trying ${
      uniqueVariations.length
    } path variations for '${filePath}': ${uniqueVariations
      .slice(0, 5)
      .join(", ")}${uniqueVariations.length > 5 ? "..." : ""}`
  );

  // Try each variation
  for (const variation of uniqueVariations) {
    if (repoData[variation]) {
      logger.debug.info(
        `OnboardingWorkflow: Found file using path variation '${variation}' for suggested path '${filePath}'`
      );
      return variation;
    }
  }

  // If no exact matches, try a more exhaustive search through all keys
  const allPaths = Object.keys(repoData);

  // Try to find a path that ends with the suggested path (handles different root structures)
  const cleanFilePath = filePath.replace(/^\/+/, "");
  for (const existingPath of allPaths) {
    const cleanExistingPath = existingPath.replace(/^\/+/, "");
    if (
      cleanExistingPath === cleanFilePath ||
      existingPath.endsWith(`/${cleanFilePath}`)
    ) {
      logger.debug.info(
        `OnboardingWorkflow: Found file using suffix match '${existingPath}' for suggested path '${filePath}'`
      );
      return existingPath;
    }
  }

  // Try fuzzy matching on path segments (helpful when folder structure differs)
  const fileSegments = segments;
  for (const existingPath of allPaths) {
    const existingSegments = existingPath.split("/").filter(Boolean);

    // Check if the last few segments match (same file structure, different root)
    if (fileSegments.length >= 2 && existingSegments.length >= 2) {
      const lastTwoFile = fileSegments.slice(-2).join("/");
      const lastTwoExisting = existingSegments.slice(-2).join("/");

      if (lastTwoFile === lastTwoExisting) {
        const repoItem = repoData[existingPath];
        if (
          repoItem &&
          (repoItem.type === "blob" ||
            repoItem.fileContent ||
            repoItem.contents)
        ) {
          logger.debug.info(
            `OnboardingWorkflow: Found file using segment match '${existingPath}' for suggested path '${filePath}'`
          );
          return existingPath;
        }
      }
    }
  }

  // As a last resort, try fuzzy matching based on filename
  const fileName = filePath.split("/").pop();
  if (fileName) {
    for (const existingPath of allPaths) {
      if (existingPath.endsWith(`/${fileName}`) || existingPath === fileName) {
        // Make sure it's a file (has content or is type blob)
        const repoItem = repoData[existingPath];
        if (
          repoItem &&
          (repoItem.type === "blob" ||
            repoItem.fileContent ||
            repoItem.contents)
        ) {
          logger.debug.info(
            `OnboardingWorkflow: Found file using filename match '${existingPath}' for suggested path '${filePath}'`
          );
          return existingPath;
        }
      }
    }
  }

  logger.debug.warn(
    `OnboardingWorkflow: Could not find any path variation for '${filePath}' after trying ${uniqueVariations.length} variations`
  );

  return null;
};
